/**
 * The ONE declaration of what an Activate destination type is.
 *
 * Before this module the same destination was declared in nine places: a union type, two parallel
 * `Record<>` maps, an `act_`-stripping special case, two `if FB … else TikTok` config builders, an
 * adoption lookup, five duplicated `z.enum([...])` literals, and — separately, on the client — a
 * second hardcoded list carrying its own copy of `supportedAccountDefinitions`. Server and client
 * were the same fact written twice, free to drift.
 *
 * Everything here is derived from {@link ACTIVATION_DESTINATION_SPECS}. Adding a destination is one
 * entry in that object plus an icon; the union, the zod enum, the account filter, the endpoint
 * routing and the wizard's type list all widen from it.
 *
 * ## Browser-safe — this bundles into the SPA
 *
 * FIVE config files alias this module as `@server/activation-destinations`, the same mechanism as
 * `@server/postgres` / `@server/clickhouse`: `ui/tsconfig.json`, `ui/tsconfig.app.json` (the one
 * `pnpm typecheck` actually reads), `ui/vite.config.ts`, `ui/vitest.config.ts` and
 * `.storybook/main.ts`. Miss one and the failure is a module-resolution error in only that tool.
 * AGENTS.md's rule for aliased modules applies: **no Node built-ins, no `Env`, no HTTP client, and
 * keep transitive deps cheap.** This file has zero imports on purpose. The service layer supplies
 * the credentials and performs the calls; the spec only says *which* call and *what shape*.
 *
 * Layer: shared/infra leaf (`src/lib`). It imports nothing and is imported by `src/services`,
 * `src/rpc` and `ui/src` alike — the only position from which server and client can share one truth.
 *
 * ## Use existing, or create on Enable (ACT2-1082)
 *
 * An `audience`-kind destination attaches to a vendor audience that already exists — and, when its
 * far side declares {@link FarSideCreateSpec} `create`, can instead create a new one on Enable, the
 * way the webapp's VDM form does. Lookout never calls a vendor itself to do it: the webapp's
 * mechanism is reused verbatim. `/setup` receives `createAudience: 'yes'` plus `audience: { name,
 * description? }`, integrations-info creates the list inside `/setup`, and the response carries the
 * new `audienceId` (see {@link baseVdmConfig} and {@link normalizeCreatedSetupData}). A destination
 * with no `create` declaration offers only the existing-audience picker, exactly as before. Editing a
 * sync never creates: the stored config is always `createAudience: 'no'` once Enable has finished.
 */

/**
 * How a spec names the platform-side account (Meta ad account, TikTok advertiser, Google customer)
 * — or says that it has none.
 *
 * `none` is not a degenerate case to route around: Iterable and Braze genuinely have no second
 * account beneath the RudderStack credential, so for them the wizard has no picker to show and
 * `listAudiences` takes no account key. Modelling that as a variant keeps every consumer honest;
 * modelling it as an empty string would have every call site quietly send `{ '': '' }`.
 *
 * Three mechanisms, because the platform genuinely has three — not because the abstraction drifted.
 */
type PlatformAccountBase = {
  /**
   * Key this id is written under, wherever it is stored. Load-bearing: adoption reads it back out
   * (see {@link adoptPlatformAccountId}), so a spec cannot spell the key differently in the two
   * directions.
   */
  configKey: string;
  /**
   * Which config blob persists the chosen id. `vdm` means it belongs to the SYNC rather than the
   * destination — LinkedIn's `accountId` is a mapping-time choice, not a credential setting.
   * `vdm-or-destination` reads the sync first and falls back to the destination, for a type whose
   * writers disagree: Reddit's webapp form puts the id on the sync, its agentic/MCP path on the
   * destination. Defaults to the destination, which is where every ad platform keeps it.
   */
  carriedIn?: 'destination' | 'vdm' | 'vdm-or-destination';
  /** Normalise a pasted id before it is sent or stored. Applied at every entry point. */
  normalize?: (raw: string) => string;
};

export type PlatformAccountSpec =
  | (PlatformAccountBase & {
      /** `POST …/integrationsInfo/:definitionName/destinationData` with this `destApi`. */
      via: 'destinationData';
      destApi: string;
    })
  | (PlatformAccountBase & {
      /**
       * `POST …/integrationsInfo/v1/:slug/:path` — an options list of the platform's own accounts,
       * the same request shape as the audience list rather than the `destinationData` shape.
       */
      via: 'listAccounts';
      path: string;
    })
  | { via: 'none' };

/**
 * What the wizard has chosen so far, as much of it as any spec callback needs.
 *
 * Passed as one object rather than positional arguments because the axes are per-destination and
 * still growing: FBCA scopes its audience list by ad account, LinkedIn scopes its by account AND
 * audience type, Iterable scopes its by nothing at all. A positional signature would have to grow a
 * parameter every time, at every call site, for a value most types ignore.
 */
export type ActivationSelection = {
  /** Platform account id, already normalised. `''` when the type has none, or none is chosen yet. */
  platformAccountId: string;
  /** Far-side audience id. `''` before one is picked. */
  farSideAudienceId: string;
  /** LinkedIn's `user` / `company` axis. Absent for types that have no such axis. */
  audienceType?: string;
  /**
   * Values carried off the chosen far-side audience — see `audiences.carries`.
   *
   * Google's remarketing list reports its own `typeOfList`, which the wizard cannot ask the user for
   * (the list already has one) but must send back on `getFields` and in the connection config.
   */
  carried?: Record<string, string>;
};

/**
 * A field on the Configure sync → Advanced panel.
 *
 * Declarative because the panel is otherwise where per-destination special cases accumulate: it
 * began as one shared question plus `isFb ? <three more fields> : null`, and Google would have made
 * that a second branch with three more. Declaring them means the panel renders a list and adding a
 * destination stays a data change.
 */
type AdvancedFieldBase = {
  /** Key in the advanced config, and usually the key sent in the vdm config. */
  name: string;
  label: string;
  description: string;
  default: string;
  /**
   * Rendered only when this holds. Google's `appId` applies to mobile-advertiser-id lists alone, and
   * which kind of list it is comes off the list itself rather than from the user.
   *
   * Widened from the bare carried values once two more conditions turned up that carried values
   * cannot express — LinkedIn's hash question, which the webapp shows for a `user` audience and
   * hides for a `company` one, and Iterable's `updateExistingUsersOnly`, which the webapp shows only
   * for a project whose type has a user id to update. Both live outside the chosen audience, so the
   * predicate takes a context and every caller must supply it.
   */
  visibleWhen?: (context: AdvancedVisibility) => boolean;
};

/**
 * Everything an Advanced field's visibility may depend on.
 *
 * One object rather than three positional arguments, so widening it again stays a change to this
 * type and its producers rather than to every predicate. `accountOptions` is the RudderStack
 * account's own `options` blob (Iterable's `projectType`, Braze's `dataCenter`), unschema'd because
 * each destination knows only its own keys.
 */
export type AdvancedVisibility = {
  /** Values carried off the chosen far-side audience. `{}` before one is chosen. */
  carried: Record<string, string>;
  /** The chosen audience-type axis, for a destination that has one (LinkedIn). */
  audienceType?: string;
  /** `options` on the chosen RudderStack account. `{}` when no account is chosen or none loaded. */
  accountOptions: Record<string, unknown>;
};

export type AdvancedFieldSpec =
  | (AdvancedFieldBase & { kind: 'select'; options: readonly { value: string; label: string }[] })
  | (AdvancedFieldBase & { kind: 'text'; placeholder?: string });

/**
 * A destination that splits its audiences into kinds, each with its own field list.
 *
 * LinkedIn is the only one so far: a `company` audience matches on organisation identifiers, a
 * `user` audience on personal ones, and the two are separate lists on the same ad account. The
 * options live here rather than in the wizard so the axis, its labels and its default are declared
 * once, next to the field tables they select between.
 */
export type AudienceTypeAxis = {
  /** Field label on the configure step. */
  label: string;
  options: readonly { value: string; label: string }[];
  /** Chosen before the user touches it, so the audience list is loadable immediately. */
  default: string;
};

/** A destination-side field a source column can be mapped to. */
export type ActivationField = {
  name: string;
  label: string;
  type?: string;
  isRequired: boolean;
};

/**
 * What a sync writes into on the far side — and there are two shapes, not one.
 *
 * `audience` is an object over there that the sync points at: a Meta custom audience, a LinkedIn
 * audience, a Google remarketing list. Either the customer already has it and picks it from a list,
 * or — for a destination that declares `create` — `/setup` creates it on Enable from a name the user
 * types (ACT2-1082).
 *
 * `attribute` is Braze, and it is genuinely different: Braze has no audience object to attach to.
 * Membership is a named custom attribute set on user profiles, and the customer then builds a Braze
 * Segment on that attribute themselves. Which means Lookout creates even less here than it does for
 * the ad platforms — there is no far-side object at all, so nothing to list, nothing to mint, and
 * nothing that can be renamed underneath us. A custom attribute exists by being written, the way a
 * column does.
 *
 * Both carry `configKey`, the key under which the sync's own `config.destination` holds the chosen
 * value. Adoption reads it back out, so a destination cannot spell it one way on write and another
 * on read.
 */
/**
 * One carried value's two names. See {@link FarSideSpec} `carries`.
 *
 * `from` is what the option-list response calls it; `to` is what the sync config calls it.
 */
export type CarriedKey = { readonly from: string; readonly to: string };

/**
 * A list-type choice the user makes when CREATING a far-side list, for a destination whose lists have
 * a fixed type (Google's Customer Match `uploadKeyType`).
 *
 * On the existing-list path the type is carried off the chosen list and never asked. On the create
 * path there is no list yet, so it is asked — and the answer is written into the SAME carried key
 * (`carriedKey`), so `emitCarried`, the `getFields` scoping and the `appId` visibility all keep reading
 * one value whichever way the list was chosen. Copied from the webapp form's `typeOfListOptions`.
 */
export type FarSideListTypeSpec = {
  /** Key in {@link ActivationSelection.carried} the choice is written under. */
  carriedKey: string;
  label: string;
  description: string;
  learnMoreUrl: string;
  options: readonly { value: string; label: string; description: string }[];
  /** Chosen as soon as the user picks "Create new", as the webapp does. */
  default: string;
};

/**
 * An Advanced field that create mode asks for UP FRONT and requires, while its own `visibleWhen`
 * holds. Google's `appId`: the webapp shows it beside the list type when creating a
 * mobile-advertising-id list and refuses to submit without it, whereas Lookout's Advanced panel
 * (collapsed) only offers it. The value still lives in the `advanced` bag under `name`.
 */
export type FarSideCreateRequiredField = {
  /** The `AdvancedFieldSpec.name` this asks for. */
  name: string;
  label: string;
  description: string;
  placeholder: string;
  requiredMessage: string;
};

/**
 * A destination whose far-side audience can be CREATED on Enable rather than only picked (ACT2-1082).
 *
 * Every value here is the webapp form's own (`src/components/common/forms/<dest>/form.tsx`), because
 * the two surfaces must say the same thing about the same platform. Present only on destinations whose
 * webapp form offers "Create new"; a destination without it renders the existing picker unchanged.
 */
export type FarSideCreateSpec = {
  /** The webapp's noun for the far-side object: Google and Iterable say "list", the rest "audience". */
  noun: 'audience' | 'list';
  /** Heading over the Create / Use existing choice. */
  title: string;
  subtitle: string;
  /**
   * Where the name (and description) inputs go. `section`: under their own "Specify new … details"
   * heading (Meta, Google, LinkedIn, TikTok). `inline`: straight under the choice (Iterable, Reddit,
   * HubSpot).
   */
  layout: 'section' | 'inline';
  /** Whether a description is asked for and sent. Reddit and HubSpot take none. */
  description: boolean;
  nameMinLength: number;
  /**
   * Reddit rejects names over 500 characters. Absent means {@link FAR_SIDE_CREATE_NAME_MAX_LENGTH},
   * Lookout's own ceiling, so the wizard and the server agree on one bound for every destination.
   */
  nameMaxLength?: number;
  /**
   * The choice's starting value. Absent means the user must pick, as every webapp form here does —
   * none of them sets an initial `createAudience` on create. HubSpot defaults to existing-list so
   * create stays an explicit choice.
   */
  defaultMode?: FarSideMode;
  /**
   * A create-mode name derived from the Lookout audience's own title. HubSpot uses this so the
   * wizard can seed the name field once; other destinations leave it blank for the user to type.
   * The wizard must not re-apply it over edits.
   */
  suggest?: (audienceName: string) => string;
  listType?: FarSideListTypeSpec;
  requiredFields?: readonly FarSideCreateRequiredField[];
};

/**
 * Lookout's ceilings on what a create sends, for every destination that states none of its own. One
 * pair, read by the wizard's inline rules, the server's `farSideCreateError` and the RPC schema alike,
 * so a too-long name is caught where it is typed rather than only at Enable.
 */
export const FAR_SIDE_CREATE_NAME_MAX_LENGTH = 1000;
export const FAR_SIDE_CREATE_DESCRIPTION_MAX_LENGTH = 2000;

/**
 * `data.code` on the Enable error for a far-side list `/setup` created but the sync then failed to
 * bind (`FarSideAudienceCreatedUnlinkedError`). The wizard reads it — with `createdAudienceId` and
 * `createdAudienceName` beside it in `data` — to switch to "Use existing" with that list selected.
 * Here, like {@link RUDDERSTACK_API_ERROR_CODE}, because the server stamps it and the client reads it.
 */
export const FAR_SIDE_CREATED_UNLINKED_ERROR_CODE = 'FAR_SIDE_CREATED_UNLINKED';

/**
 * `data.code` on the Enable error when `/setup` was asked to create and Lookout could not tell
 * whether the vendor list exists (`FarSideAudienceCreateOutcomeUnknownError`). The wizard reads it
 * to keep the typed name and require an explicit acknowledgment before another create attempt.
 */
export const FAR_SIDE_CREATE_OUTCOME_UNKNOWN_ERROR_CODE = 'FAR_SIDE_CREATE_OUTCOME_UNKNOWN';

/**
 * Controlled `errorDetails.code` values from HubSpot `/setup` when a create fails. Only
 * `hubspotListCreateRefused` is matched by equality and means nothing was created.
 * `hubspotListCreateOutcomeUnknown`, a missing code, and any other code fall through to the
 * unknown-outcome recovery. Other destinations never emit these codes.
 */
export const HUBSPOT_LIST_CREATE_REFUSED_CODE = 'hubspotListCreateRefused';
export const HUBSPOT_LIST_CREATE_OUTCOME_UNKNOWN_CODE = 'hubspotListCreateOutcomeUnknown';

/**
 * Controlled `errorDetails.code` from Pinterest `/setup` when a create made the customer list but not
 * the audience on it (Pinterest spec §6). There is nothing to pick under "Use existing" yet and a retry
 * would make a second list, so the service shows its own recovery copy, not the generic refusal.
 */
export const PINTEREST_LIST_CREATED_WITHOUT_AUDIENCE_CODE = 'pinterestListCreatedWithoutAudience';

/**
 * `data.code` on the Enable error for a `/setup` the platform refused on its merits, when it said why
 * AND the destination's spec opts in with `setupRefusalIsUserFacing` (Gainsight PX). `data.message`
 * carries that explanation (the route prefix stripped) and the Activate toast shows it as written.
 * Any other refusal — no message, or a destination whose route answers developer strings — keeps
 * {@link RUDDERSTACK_API_ERROR_CODE} and the generic copy.
 */
export const ACTIVATION_SETUP_REFUSED_ERROR_CODE = 'ACTIVATION_SETUP_REFUSED';

/** Whether this sync attaches to an existing far-side audience or creates one on Enable. */
export type FarSideMode = 'create' | 'existing';

/** What the user typed for a far-side audience created on Enable. */
export type FarSideCreateInput = { name: string; description?: string };

export type FarSideSpec =
  | {
      kind: 'audience';
      /** Path suffix. Google uses `getRemarketingLists`, not `listAudiences`. */
      path: string;
      config: (selection: ActivationSelection) => Record<string, string>;
      /**
       * Extra keys to keep off the chosen option, into {@link ActivationSelection.carried}. For a
       * value the audience already has and the user must not be asked for.
       *
       * Declared as a PAIR because the two names differ and both directions are needed: `from` is the
       * key on the option-list response (`type`, `listType`), `to` is the key `buildVdmConfig` writes
       * into the sync config (`typeOfList`, `audienceListType`). Emitting and reading both derive from
       * this one declaration — hand-writing the rename twice, once per direction, is the
       * two-copies-kept-equal-by-hand problem this registry exists to remove.
       */
      carries?: readonly CarriedKey[];
      /**
       * Which response keys hold each option's id and display label.
       *
       * The ad platforms answer `{ value, label }`, which is the default. Iterable answers
       * `{ id, name, listType }` — the webapp remaps it client-side, per destination, and so must we.
       * Declared here rather than sniffed from the payload: a reader that accepts either pair would
       * silently paper over a destination answering the wrong shape.
       */
      optionKeys?: { readonly id: string; readonly label: string };
      /**
       * A behavioural warning about the CHOSEN audience, shown above the picker, or `null` when
       * there is nothing to say. A function of the carried values rather than a constant, because
       * what needs saying depends on which audience was picked — Iterable's suppression lists invert
       * the meaning of the sync, and its standard lists do not.
       *
       * The `attribute` arm's `notice` is a plain string for the opposite reason: Braze has no
       * audience to pick, so its warning is true of every sync to it.
       */
      notice?: (carried: Record<string, string>) => string | null;
      /**
       * A fact about this destination's audiences that holds whatever is picked, shown as an info
       * callout above the picker before anything is chosen — the audience arm's counterpart of the
       * `attribute` arm's `notice`. Reddit uses it to say which audiences are listed and what Reddit
       * needs before one is usable.
       */
      about?: string;
      /** Present when this destination can create its far-side audience on Enable. */
      create?: FarSideCreateSpec;
      /**
       * What the far-side object is called, for a destination WITHOUT `create` — one with `create`
       * names it there, and only there. Absent = "audience". Marketo says "list": its webapp form
       * offers existing static lists only, so it has no create block to carry the noun.
       */
      noun?: FarSideCreateSpec['noun'];
      /**
       * The far-side list is fixed for the life of the activation (HubSpot, Marketo). An edit may
       * change mappings and schedule, never the list: the edit sheet shows the picker read-only, and
       * the server refuses to open or save an edit whose live list differs from the recorded one, or
       * that asks for another. Another list needs a new activation; earlier memberships stay. The
       * audience arm's counterpart of the attribute arm's `lockedAfterCreate`, enforced differently.
       */
      listFixedOnEdit?: boolean;
      /**
       * What one list member is called on the platform, for the fixed-list notice — HubSpot lists
       * hold contacts, Marketo lists hold leads. Absent = "member".
       */
      memberNoun?: string;
      /**
       * `'server'`: the far-side catalogue is too large to load whole, so the picker searches and
       * pages on the server (`audience.searchActivationAudiences`) instead of filtering one full list
       * in the browser. Marketo: Cvent's 28k static lists take ~50 s to walk, past the 30 s client
       * timeout. Absent = the whole list is loaded once, as before.
       */
      listSearch?: 'server';
      configKey: string;
    }
  | {
      kind: 'attribute';
      label: string;
      description: string;
      placeholder: string;
      /**
       * Legal shape of the name, enforced in the wizard and again on Enable. A `RegExp` rather than a
       * string so no caller has to build one from a non-literal — and it is a compile-time constant,
       * never user input.
       */
      pattern: RegExp;
      maxLength: number;
      configKey: string;
      /** A starting name derived from the Lookout audience's own title. */
      suggest: (audienceName: string) => string;
      /** A behavioural fact the user needs before choosing, shown above the field. */
      notice: string;
      /**
       * The attribute cannot be renamed once the activation exists. rudder-sources runs a full
       * resync only when column mappings change, and the attribute name is not a mapping: a rename
       * would leave current members without `true` on the new attribute and the old one `true`
       * forever. The edit sheet shows the field read-only and the server refuses a changed name.
       * Absent = editable (Braze shares the latent issue; it is tracked separately).
       */
      lockedAfterCreate?: boolean;
    };

/**
 * Where the destination-side field list comes from.
 *
 * Not always an endpoint: LinkedIn's fields are a fixed table that varies by `audienceType`, which
 * the webapp hardcodes client-side and never asks the platform for. Modelling that as `via: 'static'`
 * keeps the service from making a call that would 404, and keeps the wizard from showing a spinner
 * for data it already has.
 */
export type FieldsSpec =
  | {
      via: 'endpoint';
      /** Path suffix under `…/integrationsInfo/v1/:slug/`. Not always `listFields` — Google uses `getFields`. */
      path: string;
      /** Extra `config` for the call. FBCA passes the far-side `audienceId` (LOOKALIKE_VALUE rule). */
      config: (selection: ActivationSelection) => Record<string, string>;
      /**
       * `true` when the endpoint's rows feed BOTH tables: rows with `isUnique: true` are identifiers,
       * every other row is a trait (the second table). Absent = every row is an identifier, which is
       * every endpoint destination but Gainsight PX — their `listFields` routes do not mark identifiers
       * with `isUnique` reliably.
       *
       * The membership attribute (`farSideAudienceId`) is dropped from the extras by the CLIENT
       * (`traitOptionFields`), and the fields query is NOT keyed on it (`fieldsQueryFarSideAudienceId`),
       * so typing the attribute name never re-asks the platform.
       */
      splitsByIsUnique?: true;
    }
  | {
      via: 'static';
      /** Fields a source column may be mapped to as an IDENTIFIER (`identifierMappings`). */
      identifiers: (selection: ActivationSelection) => ActivationField[];
      /**
       * Fields for the SECOND mapping table (`fieldMappings`) — attributes rather than identities.
       * Only LinkedIn has one; absent means the destination has a single mapping table.
       */
      extras?: (selection: ActivationSelection) => ActivationField[];
    };

/** What Enable sends as the destination's `config` when Lookout mints it. */
export type DestinationConfigInput = {
  /** RudderStack account id (the credential) — never a platform token. */
  accountId: string;
  /** Platform account id, already normalised. Empty when the type has no platform account. */
  platformAccountId: string;
};

/**
 * The vdm values posted to `/setup`. `createAudience` present = create the far-side audience on
 * Enable (ACT2-1082); absent = attach to `farSideAudienceId`.
 *
 * `audienceType` belongs to LinkedIn alone; `fieldMappings` to LinkedIn and Gainsight PX. They are
 * flat optionals rather than a discriminated union on `destinationType` because the union would
 * ripple through the RPC input and the wizard state for a two-field difference; the cost of the
 * looser shape is that "LinkedIn without an audienceType" is representable, so the service rejects
 * it explicitly (`ActivationEnableIncompleteError`) rather than silently posting a partial config.
 * If a third type needs its own axis, revisit — that is the point at which the union pays.
 */
export type VdmConfigInput = {
  /** `''` when {@link VdmConfigInput.createAudience} is set — there is no audience to point at yet. */
  farSideAudienceId: string;
  /**
   * Create the far-side audience inside `/setup` instead of attaching to `farSideAudienceId`. Only
   * ever set on Enable, for a destination that declares `farSide.create`, already validated.
   */
  createAudience?: FarSideCreateInput;
  identifierMappings: { from: string; to: string }[];
  /**
   * The second mapping table's rows. Empty for a destination that has no such table. Gainsight PX's
   * `/setup` re-derives each row's attribute `type` itself (trait-mappings D19, D22), so none is held here.
   */
  fieldMappings: { from: string; to: string }[];
  /** The chosen platform account, `''` for a type that has none. */
  platformAccountId: string;
  /** LinkedIn's `user` / `company` axis. */
  audienceType?: string;
  /** Values carried off the chosen audience — see {@link ActivationSelection.carried}. */
  carried?: Record<string, string>;
  /**
   * The Advanced panel's values, keyed by `AdvancedFieldSpec.name`.
   *
   * A bag rather than a fixed shape: the fields differ per destination, and a fixed shape could only
   * ever describe one of them (it described FBCA's, and every other type carried three keys it did
   * not use). The service validates it against the spec's declared fields, which is the check the
   * fixed shape used to provide.
   */
  advanced: Record<string, string>;
};

/**
 * A sync's stored settings, read back out of its `config.destination` into the shape the wizard holds
 * them in — the inverse of {@link VdmConfigInput}, and what an edit form is seeded from.
 *
 * `platformAccountId` is deliberately ABSENT. It lives in different objects for different
 * destinations (LinkedIn's is in the sync config, everyone else's is on the destination), and
 * {@link adoptPlatformAccountId} already reads the right one off the `carriedIn` discriminant. Adding
 * it here would be a second place that has to know that split.
 */
export type VdmConfigRead = {
  farSideAudienceId: string | null;
  identifierMappings: { from: string; to: string }[];
  /** As {@link VdmConfigInput.fieldMappings}. */
  fieldMappings: { from: string; to: string }[];
  audienceType?: string;
  carried: Record<string, string>;
  advanced: Record<string, string>;
};

export type ActivationDestinationSpec = {
  /** RudderStack destination-definition name. Equal to the registry key for every type so far. */
  definitionName: string;
  /** Shown in the wizard's type list. */
  displayName: string;
  /** Short platform label for far-side copy ("Meta Ads", "TikTok Ads"). */
  platform: string;
  /** RS `accountDefinitionName`s this type accepts — the step-2 account filter. */
  accountDefinitions: readonly string[];
  /**
   * `accountDefinitionName`s this destination still RECOGNISES but Lookout no longer OFFERS.
   *
   * Selectable and manageable are two different questions. `accountDefinitions` answers the first:
   * what a user may pick for a new activation, or create. This field carries the remainder of the
   * second: names that exist on real accounts in customer workspaces, whose rows Lookout must still
   * list, edit, delete and re-authorize. Dropping a name from `accountDefinitions` alone makes
   * existing accounts of that kind vanish from Settings entirely — no edit, no delete, no
   * re-authorize, silently — while delivery keeps working, because the server resolves a
   * destination's account by id. Moving it here is what keeps those rows maintainable.
   *
   * Absent for every destination that has never retired a grant. See
   * `ACTIVATION_MANAGEABLE_ACCOUNT_DEFINITION_NAMES`.
   */
  legacyAccountDefinitions?: readonly string[];
  /** integrationsInfo slug. Lowercase for every type so far; `BRAZE_AUDIENCE` is not, when it lands. */
  slug: string;
  platformAccount: PlatformAccountSpec;
  /** Present only for a destination whose audiences come in kinds. See {@link AudienceTypeAxis}. */
  audienceTypes?: AudienceTypeAxis;
  /** What this destination's sync actually writes into. See {@link FarSideSpec}. */
  farSide: FarSideSpec;
  fields: FieldsSpec;
  /**
   * Label for the platform-account input on the configure step. Absent for a type whose
   * `platformAccount` is `none` — there is no input to label.
   */
  platformAccountLabel?: string;
  /** Add-row label on Map identifiers (webapp `VisualMapperSection.addMoreLabel`). */
  mappingAddLabel: string;
  /**
   * One line under Map identifiers on how this destination matches (ACT2-1287). Written for people,
   * from the destination's `options.audienceActivation.mappingGuidance` in rudder-integrations-config,
   * which is written for agents ("use these names verbatim as `to`") and so is never shown as-is.
   * Re-read that guidance when it changes. Absent: no hint.
   */
  mappingHint?: string;
  /**
   * Whether Lookout offers to hash PII before sending to this destination.
   *
   * True for every ad platform and ESP that matches on hashed identifiers. FALSE for Braze,
   * which matches on a plain `external_id` and whose connection config has no such key —
   * sending one would be a field the webapp never sends and the transformer never reads.
   *
   * Declared rather than assumed because the question is prepended to every panel from the
   * spec's own `platform`, so "every destination asks it" had to become "every destination
   * that hashes it".
   */
  hashesPii: boolean;
  /**
   * When the hash question is ASKED, for a destination that hashes only some of the time.
   *
   * Separate from `hashesPii` because the two are different facts: LinkedIn hashes, so the key is
   * always written, but the webapp only puts the question in front of the user for a `user`
   * audience. Absent means "always asked", which is every destination but LinkedIn.
   */
  hashesPiiWhen?: (context: AdvancedVisibility) => boolean;
  /**
   * The hash question's help text, when the shared sentence would misdescribe this destination.
   *
   * The shared copy names "emails and phone numbers". Reddit takes email and mobile advertising IDs
   * and no phone number at all, so for it the shared sentence is false rather than merely generic.
   * Must still name {@link platform}.
   */
  hashesPiiDescription?: string;
  /**
   * The fields the hash question governs, by their exact `to` name: the ones rudder-transformer hashes
   * when `isHashRequired` is on and refuses unhashed when it is off (`hashingType` other than NONE).
   * Each takes SHA-256 unless its name names another algorithm (LinkedIn's `sha512Email`). A field
   * whose name names an algorithm is treated as hashed even when missing here; any other field not
   * listed is sent as is (Meta's `MADID` and `EXTERN_ID`, LinkedIn's `googleAid`). Read by the wizard's
   * suggestions and hashing check (ACT2-1287); re-read the transformer when a destination's fields change.
   */
  hashedFields?: readonly string[];
  /** The rest of the Advanced panel, declared rather than branched on. */
  advancedFields: readonly AdvancedFieldSpec[];
  /**
   * A SECOND mapping table — attributes sent alongside the identity rather than matched on.
   *
   * Its copy lives here, not in the component, so adding a destination stays a data change. Keyed on
   * its own presence rather than on `fields.via === 'static'`: the two happen to coincide today
   * (LinkedIn is the only destination with a second table, and its fields are static), but they are
   * unrelated facts and welding them would make a fetched-fields destination with a second table
   * inexpressible. Gainsight PX is the fetched-fields destination with a second table: its endpoint
   * arm declares `splitsByIsUnique`.
   */
  extraMappings?: {
    label: string;
    /** What the run detail's connection band calls the stored rows (`Traits`, `Attributes`). */
    detailLabel: string;
    description: string;
    addLabel: string;
    /** Distinguishes this table's accessible row names from the identifier table's. */
    rowNoun: string;
    /**
     * Shown above the table whenever it has no options to offer, whatever the cause — Gainsight PX
     * answers `identifyId` alone when PX is unreachable, the key is wrong or no custom attribute is
     * enabled (trait-mappings §5.1).
     */
    unavailableNotice?: string;
  };
  /**
   * Whether this destination's `/setup` refusal text is written for a person, so the Enable or Save
   * error shows it as the platform wrote it (route prefix stripped, PAT scrubbed).
   *
   * Opt-in because most integrations-info routes answer developer strings ("Access Token is
   * required"); those keep the generic "<platform> did not accept the sync" copy. True only where the
   * route was written to explain itself — Gainsight PX ("No Gainsight PX user attribute has this API
   * name"). Absent means false.
   */
  setupRefusalIsUserFacing?: boolean;
  /**
   * Lookout never adopts an unrecorded connection of this type, even on a source a Lookout audience
   * owns (HubSpot spec §2.1; Marketo follows it). A connection built in the webapp stays independent:
   * recording it would put a customer-authored destination within reach of Lookout's delete path.
   * Absent means adoptable — see {@link adoptsUnrecordedConnections}.
   */
  neverAdopt?: boolean;
  buildDestinationConfig: (input: DestinationConfigInput) => Record<string, unknown>;
  buildVdmConfig: (input: VdmConfigInput) => Record<string, unknown>;
  /**
   * The inverse of {@link ActivationDestinationSpec.buildVdmConfig} — reads a sync's stored settings
   * back out for an edit form to seed from.
   *
   * Takes the SYNC's `config.destination` and nothing else, on purpose. FBCA declares
   * `isHashRequired`, `disableFormat`, `type`, `subType` and `audienceId` in BOTH that blob and the
   * destination's own config, with different meanings — the destination's are hardcoded constants
   * (`isHashRequired: true`, `type: 'NA'`). An inverse handed the wrong blob therefore returns a
   * plausible constant rather than an error, prefilling "hash: yes, type: NA" over the user's real
   * settings. Keeping the destination config out of the signature makes that unrepresentable rather
   * than merely discouraged.
   *
   * **Absent-key convention:** a key that is missing, or present-but-empty, is reported as ABSENT —
   * omitted from `carried` / `advanced`, `null` for `farSideAudienceId`. Callers must not read an
   * absent key as a default: `assertAdvancedMatchesSpec` fills advanced defaults from the spec, and
   * an absent carried value means "this sync was written without one", which is a different fact from
   * "it carries an empty string". Distinguishing the two is what lets Google's `typeOfList` be
   * repaired rather than silently re-sent as `''`.
   */
  readVdmConfig: (syncDestinationConfig: Record<string, unknown>) => VdmConfigRead;
};

const YES_NO: readonly { value: string; label: string }[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

/**
 * A Braze attribute name suggested from the Lookout audience's own title.
 *
 * `rs_`-prefixed and stripped of everything the attribute-name rule rejects, matching the webapp's
 * own suggestion so the same audience proposes the same name on both surfaces.
 */
export function suggestBrazeAttributeName(audienceName: string): string {
  const sanitized = audienceName.replace(/\W/g, '').slice(0, 255);
  if (sanitized.length === 0) return '';
  return sanitized.startsWith('rs_') ? sanitized : `rs_${sanitized}`.slice(0, 255);
}

/**
 * A HubSpot static-contact-list name suggested from the Lookout audience's own title.
 *
 * Trimmed and capped at Lookout's create-name ceiling — HubSpot list names are free text, so there
 * is no Braze-style sanitisation. Empty after trim means the wizard leaves the field blank.
 */
export function suggestHubSpotListName(audienceName: string): string {
  return audienceName.trim().slice(0, FAR_SIDE_CREATE_NAME_MAX_LENGTH);
}

/** Turn a list of bare enum values into select options labelled by themselves. */
function enumOptions(values: readonly string[]): readonly { value: string; label: string }[] {
  return values.map((value) => ({ value, label: value }));
}

/**
 * The one question every destination asks. Written once and parameterised by platform, because it
 * names the vendor the data is about to be sent to — and a two-way branch getting that wrong is a
 * defect this panel has already shipped once.
 */
function hashPiiField(
  platform: string,
  visibleWhen?: AdvancedFieldSpec['visibleWhen'],
  description?: string,
): AdvancedFieldSpec {
  return {
    kind: 'select',
    name: 'isHashRequired',
    label: 'Automatically hash PII data?',
    description:
      description ??
      `RudderStack will hash sensitive fields like emails and phone numbers before syncing to ${platform}`,
    default: 'yes',
    options: YES_NO,
    ...(visibleWhen !== undefined ? { visibleWhen } : {}),
  };
}

/** Read a string off an unschema'd config blob, or `null`. Shared by the adoption lookups. */
function configString(config: Record<string, unknown>, key: string): string | null {
  const value = config[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * The vdm fields every audience destination sends.
 *
 * The far-side half is one of two shapes, and they match the webapp's two form states:
 *
 * - attach (`CreateExistingFormState`): `createAudience: 'no'` + `audienceId`.
 * - create (`CreateNewFormState`): `createAudience: 'yes'` + `audience: { name, description? }`, and
 *   NO `audienceId` key at all. That absence is load-bearing, not tidy: LinkedIn's `/setup` answers
 *   `{ audienceId: <new id>, ...vdmConfig }`, spreading the request AFTER the new id, so any
 *   `audienceId` sent here — even `''` — would overwrite the id LinkedIn just minted. The webapp never
 *   sends one in create mode either: the field is not mounted.
 *
 * `syncMode: 'mirror'` matches every webapp audience form.
 */
function baseVdmConfig(input: VdmConfigInput): Record<string, unknown> {
  const farSide: Record<string, unknown> = input.createAudience
    ? {
        createAudience: 'yes',
        audience: {
          name: input.createAudience.name,
          ...(input.createAudience.description !== undefined ? { description: input.createAudience.description } : {}),
        },
      }
    : { createAudience: 'no', audienceId: input.farSideAudienceId };
  return {
    ...farSide,
    syncMode: 'mirror',
    // Emitted only by a destination that HASHES. Setting `hashesPii: false` removes the question
    // from the panel, but the key would still be written here — from an `advanced` bag that no
    // longer contains it, so it would land as a confident `false` the destination never asked for.
    // Braze escaped this only because it declares its own builder rather than using this one.
    ...(input.advanced['isHashRequired'] !== undefined
      ? { isHashRequired: input.advanced['isHashRequired'] === 'yes' }
      : {}),
    identifierMappings: input.identifierMappings.map((row) => ({ from: row.from, to: row.to })),
  };
}

/**
 * Emit the carried values a spec declares, under the config names it declares them by.
 *
 * A carried value with no value is OMITTED rather than written as `''`. Google's builder used to send
 * `typeOfList: carried?.['type'] ?? ''`, which was doubly bad: `getRemarketingLists` returns
 * `type: null` for Google's stock rule-based lists (the key is always PRESENT and null, so a
 * presence check never fires), and `getFields` treats `''`, `null`, omitted and outright garbage
 * identically — `success: true` with the same six contact-info fields. So the `''` was persisted into
 * the connection config as a scope nobody chose and nothing could detect. Omitting is byte-identical
 * at the endpoint and leaves "no scope recorded" recoverable, which `readVdmConfig` needs.
 */
function emitCarried(
  carries: readonly CarriedKey[],
  carried: Record<string, string> | undefined,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of carries) {
    const value = carried?.[key.from];
    if (typeof value === 'string' && value.length > 0) out[key.to] = value;
  }
  return out;
}

/**
 * Google's remarketing list carries its own `type` (Customer Match's `uploadKeyType`), which the sync
 * config calls `typeOfList`. Named constants rather than inline literals so the emit side, the read
 * side and the spec declaration are provably the same object.
 */
const GOOGLE_CARRIES: readonly CarriedKey[] = [{ from: 'type', to: 'typeOfList' }];

/** Iterable's Standard-vs-Suppression `listType`, stored on the sync as `audienceListType`. */
const ITERABLE_CARRIES: readonly CarriedKey[] = [{ from: 'listType', to: 'audienceListType' }];

/** The carried `listType` whose semantics invert the sync. Named so the warning cannot drift. */
const ITERABLE_SUPPRESSION_LIST_TYPE = 'Suppression';

/**
 * Iterable project types that have a user id distinct from the email, and so can meaningfully be
 * told to update existing users only. Verbatim from the webapp's own gate.
 */
const ITERABLE_UPDATE_ONLY_PROJECT_TYPES = new Set(['hybrid', 'userId-based']);

/** The inverse of {@link emitCarried} — config names back to option-response names. */
function readCarried(carries: readonly CarriedKey[], config: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of carries) {
    const value = configString(config, key.to);
    if (value !== null) out[key.from] = value;
  }
  return out;
}

/**
 * Mapping rows out of a stored config. Anything that is not a `{from, to}` pair of non-empty strings
 * is dropped rather than coerced: these rows name warehouse columns and destination fields, and a
 * half-formed row would render as a mapping the user never made.
 */
function readMappings(config: Record<string, unknown>, key: string): { from: string; to: string }[] {
  const rows = config[key];
  if (!Array.isArray(rows)) return [];
  const out: { from: string; to: string }[] = [];
  for (const row of rows) {
    if (typeof row !== 'object' || row === null) continue;
    const from = configString(row as Record<string, unknown>, 'from');
    const to = configString(row as Record<string, unknown>, 'to');
    if (from !== null && to !== null) out.push({ from, to });
  }
  return out;
}

/** The inverse of {@link baseVdmConfig}. Every spec but Braze builds on it, so every read starts here. */
function baseReadVdmConfig(config: Record<string, unknown>, farSideKey: string): VdmConfigRead {
  const advanced: Record<string, string> = {};
  // Read back as the `'yes'`/`'no'` the Advanced panel speaks, and ONLY when the key is present — a
  // destination that does not hash never wrote it, and inventing `'no'` here would put a question in
  // the panel that this destination does not ask.
  const hash = config['isHashRequired'];
  if (typeof hash === 'boolean') advanced['isHashRequired'] = hash ? 'yes' : 'no';
  return {
    farSideAudienceId: configString(config, farSideKey),
    identifierMappings: readMappings(config, 'identifierMappings'),
    fieldMappings: [],
    carried: {},
    advanced,
  };
}

/**
 * Copy declared advanced keys out of a stored config, under the same names the panel uses.
 *
 * Only keys that are present and non-empty land, per the absent-key convention on
 * {@link ActivationDestinationSpec.readVdmConfig}.
 */
function readAdvanced(config: Record<string, unknown>, keys: readonly string[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of keys) {
    const value = configString(config, key);
    if (value !== null) out[key] = value;
  }
  return out;
}

/**
 * LinkedIn's field lists, copied from the webapp form (`linkedInAudience/types.ts`), which holds them
 * client-side rather than fetching them. Split two ways: `user` and `company` audiences see entirely
 * different fields, and within each, identity fields (the match key) are a different table from
 * attribute fields (the second mapping table).
 *
 * `isRequired: false` throughout — the webapp marks none of them required. "At least one identifier"
 * is a count rule the wizard enforces, not a per-field flag.
 */
const LINKEDIN_USER_IDENTIFIERS: ActivationField[] = [
  { name: 'sha256Email', label: 'Email (SHA256)', type: 'string', isRequired: false },
  { name: 'sha512Email', label: 'Email (SHA512)', type: 'string', isRequired: false },
  { name: 'googleAid', label: 'Google Advertising ID', type: 'string', isRequired: false },
];

const LINKEDIN_USER_EXTRAS: ActivationField[] = [
  { name: 'firstName', label: 'First Name', type: 'string', isRequired: false },
  { name: 'lastName', label: 'Last Name', type: 'string', isRequired: false },
  { name: 'title', label: 'Title', type: 'string', isRequired: false },
  { name: 'company', label: 'Company', type: 'string', isRequired: false },
  { name: 'country', label: 'Country', type: 'string', isRequired: false },
];

const LINKEDIN_COMPANY_IDENTIFIERS: ActivationField[] = [
  { name: 'companyName', label: 'Company Name', type: 'string', isRequired: false },
  { name: 'organizationUrn', label: 'Organization URN', type: 'string', isRequired: false },
  { name: 'companyWebsiteDomain', label: 'Company Website Domain', type: 'string', isRequired: false },
  { name: 'companyEmailDomain', label: 'Company Email Domain', type: 'string', isRequired: false },
  { name: 'companyPageUrl', label: 'Company Page Url', type: 'string', isRequired: false },
];

const LINKEDIN_COMPANY_EXTRAS: ActivationField[] = [
  { name: 'stockSymbol', label: 'Stock Symbol', type: 'string', isRequired: false },
  { name: 'industries', label: 'Industries', type: 'string', isRequired: false },
  { name: 'city', label: 'City', type: 'string', isRequired: false },
  { name: 'state', label: 'State', type: 'string', isRequired: false },
  { name: 'country', label: 'Country', type: 'string', isRequired: false },
  { name: 'postalCode', label: 'Postal Code', type: 'string', isRequired: false },
];

/** Webapp `DataOrigin` — values sent on the FBCA destination config. */
const FBCA_DATA_ORIGIN = [
  'UNKNOWN',
  'FILE_IMPORTED',
  'EVENT_BASED',
  'SEED_BASED',
  'THIRD_PARTY_IMPORTED',
  'COPY_PASTE',
  'CONTACT_IMPORTER',
  'HOUSEHOLD_AUDIENCE',
  'NA',
];

/** Webapp `IdentifierType` — values sent on the FBCA destination config. */
const FBCA_IDENTIFIER_TYPE = [
  'ANYTHING',
  'NOTHING',
  'HASHES',
  'USER_IDS',
  'HASHES_OR_USER_IDS',
  'MOBILE_ADVERTISER_IDS',
  'FB_EVENT_SIGNALS',
  'EXTERNAL_IDS',
  'MULTI_HASHES',
  'TOKENS',
  'EXTERNAL_IDS_MIX',
  'WEB_PIXEL_HITS',
  'MOBILE_APP_EVENTS',
  'MOBILE_APP_COMBINATION_EVENTS',
  'VIDEO_EVENTS',
  'WEB_PIXEL_COMBINATION_EVENTS',
  'IG_BUSINESS_EVENTS',
  'MULTI_DATA_EVENTS',
  'STORE_VISIT_EVENTS',
  'INSTANT_ARTICLE_EVENTS',
  'ENGAGEMENT_EVENT_USERS',
  'FACEBOOK_WIFI_EVENTS',
  'CUSTOM_AUDIENCE_USERS',
  'S_EXPR',
  'DYNAMIC_RULE',
  'CONVERSION_PIXEL_HITS',
  'APP_USERS',
  'CAMPAIGN_CONVERSIONS',
  'WEB_PIXEL_HITS_CUSTOM_AUDIENCE_USERS',
  'MOBILE_APP_CUSTOM_AUDIENCE_USERS',
  'VIDEO_EVENT_USERS',
  'FB_PIXEL_HITS',
  'IG_PROMOTED_POST',
  'PLACE_VISITS',
  'OFFLINE_EVENT_USERS',
  'EXPANDED_AUDIENCE',
  'SEED_LIST',
  'PARTNER_CATEGORY_USERS',
  'PAGE_SMART_AUDIENCE',
  'MULTICOUNTRY_COMBINATION',
  'PLATFORM_USERS',
  'MULTI_EVENT_SOURCE',
  'SMART_AUDIENCE',
  'LOOKALIKE_PLATFORM',
  'SIGNAL_SOURCE',
  'MAIL_CHIMP_EMAIL_HASHES',
  'CONSTANT_CONTACTS_EMAIL_HASHES',
  'COPY_PASTE_EMAIL_HASHES',
  'CONTACT_IMPORTER',
  'DATA_FILE',
  'NA',
];

/**
 * Google Ads consent, on both axes. The same four values asked twice — the platform treats consent
 * to use the data and consent to personalise ads as separate answers.
 */
const GOOGLE_CONSENT_OPTIONS = enumOptions(['UNSPECIFIED', 'UNKNOWN', 'GRANTED', 'DENIED']);

/**
 * The Google list type that needs an app id.
 *
 * `typeOfList` itself is carried off the chosen remarketing list rather than asked when attaching: an
 * existing list already HAS a type, and offering the field would imply it can be changed, which it
 * cannot — the webapp says exactly that. It is still sent (the transformer needs it) and it scopes
 * `getFields`, which is why it is carried rather than dropped. When CREATING a list it is asked
 * ({@link GOOGLE_LIST_TYPE}) and written into the same carried key.
 */
const GOOGLE_MOBILE_LIST_TYPE = 'mobileDeviceID';

/**
 * Google's list types, verbatim from the webapp (`googleAdwordsRemarketingList/types.ts`
 * `typeOfListOptions`): the values are what the transformer reads, the labels are Google's own
 * `CustomerMatchUploadKeyType` names. `General` is the webapp's default when "Create new list" is
 * picked (`handleCreateAudienceChange` → `typeOfListOptions[0]`).
 */
const GOOGLE_LIST_TYPE: FarSideListTypeSpec = {
  carriedKey: 'type',
  label: 'Select user list type',
  description: 'User list type determines available identifiers that can be uploaded to Google Ads.',
  learnMoreUrl:
    'https://developers.google.com/google-ads/api/reference/rpc/v23/CustomerMatchUploadKeyTypeEnum.CustomerMatchUploadKeyType',
  options: [
    { value: 'General', label: 'CONTACT_INFO', description: 'Email, phone, and address identifiers' },
    // Deliberately NOT the webapp's "Your internal customer IDs": that reads as "any id you have", while
    // a CRM_ID list only matches ids already shared with Google — so it steers people off the default.
    {
      value: 'userID',
      label: 'CRM_ID',
      description: 'Only IDs you already share with Google. Most lists use Contact info.',
    },
    { value: GOOGLE_MOBILE_LIST_TYPE, label: 'MOBILE_ADVERTISING_ID', description: 'Mobile device IDs (IDFA/GAID)' },
  ],
  default: 'General',
};

/** The webapp's subtitle copy for the four destinations that say "audience" and ask in a section. */
const USE_EXISTING_OR_CREATE_AUDIENCE = 'Use an existing audience or create a new one';

/**
 * `satisfies` rather than `:` — the annotation would widen the keys to `string` and the union below
 * would collapse to `string`, which is the whole point of deriving it here.
 */
export const ACTIVATION_DESTINATION_SPECS = {
  BRAZE_AUDIENCE: {
    definitionName: 'BRAZE_AUDIENCE',
    displayName: 'Braze Audience',
    platform: 'Braze',
    accountDefinitions: ['DESTINATION_BRAZE_AUDIENCE_API_KEY'],
    // Uppercase because the webapp's URL constant is, but the route is case-insensitive: mini's
    // real integrations-info answers 200 to `BRAZE_AUDIENCE`, `braze_audience` and `BrAzE_AuDiEnCe`
    // alike, while a nonsense slug is refused — so the 200s are routing, not a catch-all. An earlier
    // comment here called this "the one destination whose slug is the UPPERCASE definition name",
    // which asserted a distinction the service does not make. Left uppercase only to match the
    // webapp verbatim; lowercase would work identically.
    slug: 'BRAZE_AUDIENCE',
    // The REST key and data centre live on the account, so Lookout never handles the credential.
    platformAccount: { via: 'none' },
    farSide: {
      kind: 'attribute',
      label: 'Audience attribute name',
      description: 'Braze custom attribute used to store membership for this sync',
      placeholder: 'rs_my_audience',
      // Letters, numbers and underscores.
      pattern: /^\w+$/,
      maxLength: 255,
      configKey: 'customAttributeName',
      suggest: suggestBrazeAttributeName,
      notice:
        'Mirror mode syncs membership continuously. Someone who leaves this audience has the attribute set to false in Braze, rather than being removed.',
    },
    fields: {
      via: 'endpoint',
      path: 'listFields',
      // Resolved from the account credential alone, like Iterable.
      config: () => ({}),
    },
    mappingAddLabel: 'Map another field',
    // Matches on a plain `external_id`, so there is nothing to hash and nothing to ask.
    hashesPii: false,
    advancedFields: [],
    buildDestinationConfig: ({ accountId }) => ({
      rudderAccountId: accountId,
      connectionMode: { warehouse: 'cloud' },
    }),
    // No `createAudience` and no `audienceId`: there is no far-side audience object to attach to or
    // create. `syncMode: 'mirror'` and the identifier mapping come from the shared base.
    buildVdmConfig: (input) => ({
      syncMode: 'mirror',
      customAttributeName: input.farSideAudienceId,
      identifierMappings: input.identifierMappings.map((row) => ({ from: row.from, to: row.to })),
    }),
    // Not `baseReadVdmConfig`: Braze's builder does not use `baseVdmConfig` either. It writes no
    // `audienceId`, no `createAudience`, and no `isHashRequired` — it does not hash — so reading
    // through the base would invent an `isHashRequired` answer for a question Braze never asks.
    readVdmConfig: (config) => ({
      farSideAudienceId: configString(config, 'customAttributeName'),
      identifierMappings: readMappings(config, 'identifierMappings'),
      fieldMappings: [],
      carried: {},
      advanced: {},
    }),
  },
  FB_CUSTOM_AUDIENCE: {
    definitionName: 'FB_CUSTOM_AUDIENCE',
    displayName: 'Facebook Custom Audience',
    platform: 'Meta Ads',
    accountDefinitions: ['DESTINATION_FB_CUSTOM_AUDIENCE_ACCESS_TOKEN'],
    slug: 'fb_custom_audience',
    platformAccount: {
      via: 'destinationData',
      destApi: 'adAccounts',
      configKey: 'adAccountId',
      // Meta's listAudiences prefixes `act_` itself, so a pasted `act_123` would become `act_act_123`.
      normalize: (raw) => {
        const trimmed = raw.trim();
        return trimmed.toLowerCase().startsWith('act_') ? trimmed.slice(4) : trimmed;
      },
    },
    farSide: {
      kind: 'audience',
      path: 'listAudiences',
      configKey: 'audienceId',
      config: ({ platformAccountId }) => ({ adAccountId: platformAccountId }),
      // `facebookCustomAudience/form.tsx`. integrations-info creates it on the ad account the
      // destination config names (`act_<adAccountId>/customaudiences`).
      create: {
        noun: 'audience',
        title: 'Select audience',
        subtitle: USE_EXISTING_OR_CREATE_AUDIENCE,
        layout: 'section',
        description: true,
        nameMinLength: 3,
      },
    },
    fields: {
      via: 'endpoint',
      path: 'listFields',
      // The id only when there is one — the webapp's own rule (`shouldCreateNewAudience === 'no' &&
      // audienceId ? { audienceId } : {}`). With no id, `listFields` answers Meta's default fields,
      // which is what a create-mode sync maps against: there is no audience to ask about yet.
      config: ({ farSideAudienceId }): Record<string, string> =>
        farSideAudienceId.length > 0 ? { audienceId: farSideAudienceId } : {},
    },
    platformAccountLabel: 'Ad Account Id',
    mappingAddLabel: 'Add mapping',
    mappingHint:
      'Map every identifier you have: email, phone, mobile ad ID or your own ID. Name and address fields only help when sent together.',
    hashesPii: true,
    hashedFields: ['EMAIL', 'PHONE', 'GEN', 'DOBY', 'DOBM', 'DOBD', 'LN', 'FN', 'FI', 'COUNTRY', 'ZIP', 'ST', 'CT'],
    advancedFields: [
      {
        kind: 'select',
        name: 'formatData',
        label: 'Normalise data formatting?',
        description: 'Meta Ads will automatically clean and standardise audience data such as emails and phone numbers',
        default: 'yes',
        options: YES_NO,
      },
      {
        kind: 'select',
        name: 'type',
        label: 'Specify where your data originates from',
        description: 'This helps Meta Ads classify the user list and apply the appropriate matching and privacy rules',
        default: 'FILE_IMPORTED',
        options: enumOptions(FBCA_DATA_ORIGIN),
      },
      {
        kind: 'select',
        name: 'subtype',
        label: 'Specify identifier type',
        description:
          'Specify the type of data Meta Ads should use to match your users to their profiles, such as email or phone number',
        default: 'MULTI_HASHES',
        options: enumOptions(FBCA_IDENTIFIER_TYPE),
      },
    ],
    buildDestinationConfig: ({ accountId, platformAccountId }) => ({
      rudderAccountId: accountId,
      adAccountId: platformAccountId,
      audienceId: '',
      userSchema: ['EMAIL'],
      isHashRequired: true,
      isRaw: false,
      disableFormat: false,
      type: 'NA',
      subType: 'NA',
      connectionMode: { warehouse: 'cloud' },
    }),
    buildVdmConfig: (input) => ({
      ...baseVdmConfig(input),
      disableFormat: input.advanced['formatData'] === 'no',
      // No `?? fallback`: the service fills every declared key from the spec's own defaults before
      // this runs, and a second default here disagreed with the first (`''` vs `FILE_IMPORTED`).
      type: input.advanced['type'],
      subtype: input.advanced['subtype'],
    }),
    readVdmConfig: (config) => {
      const read = baseReadVdmConfig(config, 'audienceId');
      // `formatData` is a NEGATION as well as a rename: the panel asks "normalise?" and the config
      // stores "disabled?". Inverting is not optional, and the default arm hides a wrong inverse —
      // `formatData: 'yes'` maps to `disableFormat: false`, so an inverse that simply mapped
      // `false -> 'yes'` without negating agrees with this one on the default and disagrees on the
      // other arm. That is why the round-trip test uses non-default values.
      const disableFormat = config['disableFormat'];
      if (typeof disableFormat === 'boolean') read.advanced['formatData'] = disableFormat ? 'no' : 'yes';
      return { ...read, advanced: { ...read.advanced, ...readAdvanced(config, ['type', 'subtype']) } };
    },
  },
  GAINSIGHT_PX_AUDIENCE: {
    definitionName: 'GAINSIGHT_PX_AUDIENCE',
    displayName: 'Gainsight PX Audience',
    platform: 'Gainsight PX',
    accountDefinitions: ['DESTINATION_GAINSIGHT_PX_AUDIENCE'],
    slug: 'gainsight_px_audience',
    // The REST key and data center live on the account, so Lookout never handles the credential.
    platformAccount: { via: 'none' },
    farSide: {
      kind: 'attribute',
      label: 'Attribute API name',
      description: 'API name of the Boolean custom user attribute in Gainsight PX that stores membership for this sync',
      placeholder: 'rs_my_audience',
      pattern: /^[A-Za-z0-9_]+$/,
      maxLength: 200,
      configKey: 'customAttributeName',
      suggest: (name) => suggestBrazeAttributeName(name).slice(0, 200),
      // Renaming would split membership across two PX attributes; see `lockedAfterCreate`.
      lockedAfterCreate: true,
      notice:
        'Create this attribute in Gainsight PX first (Administration › Attributes › User, type Boolean). Mirror mode keeps it in sync: someone who leaves this audience has it set to false. Members who are not yet Gainsight PX users are reported as failed rows until they exist in Gainsight PX; no users are created.',
    },
    // One listFields call feeds both tables: `identifyId` (isUnique) maps the user, every enabled
    // custom attribute is a trait option — minus the membership attribute itself (trait-mappings D20).
    fields: {
      via: 'endpoint',
      path: 'listFields',
      config: () => ({}),
      splitsByIsUnique: true,
    },
    mappingAddLabel: 'Map another field',
    // Matches on a plain identifyId (case-sensitive), so there is nothing to hash.
    hashesPii: false,
    advancedFields: [],
    extraMappings: {
      label: 'Traits (optional)',
      detailLabel: 'Traits',
      description:
        'Written for audience members on every sync. When someone leaves the audience only the membership attribute changes; traits keep their last value.',
      addLabel: 'Map another trait',
      rowNoun: 'trait mapping',
      unavailableNotice:
        'No Gainsight PX custom user attributes to map. Check the account and Administration › Attributes › User, then reopen this form.',
    },
    // integrations-info's gainsight_px_audience route names what is wrong with the attribute.
    setupRefusalIsUserFacing: true,
    buildDestinationConfig: ({ accountId }) => ({
      rudderAccountId: accountId,
      connectionMode: { warehouse: 'cloud' },
    }),
    buildVdmConfig: (input) => ({
      syncMode: 'mirror',
      customAttributeName: input.farSideAudienceId,
      identifierMappings: input.identifierMappings.map((row) => ({ from: row.from, to: row.to })),
      // Always written, `[]` included: an edit saves `{ ...stored, ...setupData }`, so an omitted key
      // would keep traits the user removed. `/setup` re-derives each `type` from PX (D19, D22).
      fieldMappings: input.fieldMappings,
    }),
    readVdmConfig: (config) => ({
      farSideAudienceId: configString(config, 'customAttributeName'),
      identifierMappings: readMappings(config, 'identifierMappings'),
      fieldMappings: readMappings(config, 'fieldMappings'),
      carried: {},
      advanced: {},
    }),
  },
  GOOGLE_ADWORDS_REMARKETING_LISTS: {
    definitionName: 'GOOGLE_ADWORDS_REMARKETING_LISTS',
    displayName: 'Google Ads Remarketing List',
    platform: 'Google Ads',
    // Just the plain OAuth grant. The platform also has a "DM" (Data Manager, per its own feature
    // flag `AMP_enable-garl-data-manager-account`) variant of this account definition — a second
    // OAuth scope that drives an API Activations does not use — so it is deliberately absent from
    // this list: Lookout must not offer or select it (rudder-specs `lookout-audiences-initiative/
    // activations/account-management` scope-reduction addendum). The platform already hides DM from
    // *creation* behind that flag (`isHidden` treats a `{ gate: … }` `displayOptions.hidden` as
    // hidden), so this list is what closes the other half: an existing DM account no longer being
    // *selectable* through the activation picker, which keys off these names.
    accountDefinitions: ['DESTINATION_GOOGLE_ADWORDS_REMARKETING_LISTS_OAUTH'],
    // …but production already HAS such accounts: 12 across 11 workspaces when this was checked, 9 of
    // them attached to live destinations. Unselectable was the intent; unmanageable was not, and it
    // is what dropping the name from `accountDefinitions` alone caused. Listing it here puts those
    // rows back in Settings — visible, editable, deletable, re-authorizable — without putting the
    // grant back in any picker. See `legacyAccountDefinitions` on `ActivationDestinationSpec`.
    legacyAccountDefinitions: ['DESTINATION_GOOGLE_ADWORDS_REMARKETING_LISTS_DM_OAUTH'],
    slug: 'google_adwords_remarketing_lists',
    platformAccount: {
      via: 'destinationData',
      destApi: 'customerAccounts',
      configKey: 'customerId',
    },
    farSide: {
      kind: 'audience',
      // NOT `listAudiences` — the first destination whose options route is named something else,
      // which is why the path is a spec field rather than a literal in the client.
      path: 'getRemarketingLists',
      configKey: 'audienceId',
      config: ({ platformAccountId }) => ({ customerId: platformAccountId }),
      carries: GOOGLE_CARRIES,
      // `googleAdwordsRemarketingList/form.tsx` — "list", not "audience", throughout, and the one
      // form that asks a list type (and an App ID for a mobile list) when creating.
      create: {
        noun: 'list',
        title: 'Select user list',
        subtitle: 'Use an existing list or create a new one',
        layout: 'section',
        description: true,
        nameMinLength: 3,
        listType: GOOGLE_LIST_TYPE,
        requiredFields: [
          {
            name: 'appId',
            label: 'Enter App ID',
            description:
              "App ID uniquely identifies the mobile app your data was collected from. For iOS, it's the 9-digit number at the end of the App Store URL (e.g., 476943146). For Android, it's the package name (e.g., com.labpixies.colordrips).",
            placeholder: 'Enter App ID',
            requiredMessage: 'Please enter App ID',
          },
        ],
      },
    },
    fields: {
      via: 'endpoint',
      // Also not `listFields`.
      path: 'getFields',
      // Scoped by the carried list type: a CRM_ID list matches on different fields from a
      // CONTACT_INFO one, so asking without it returns the wrong set.
      // Derived from the SAME `carries` declaration as the emit and read sides, and omitted rather
      // than sent as `''` for the same reason — `getFields` cannot tell `''`, `null`, an absent key
      // and outright garbage apart, so a defaulted scope silently returns the six contact-info
      // fields and looks exactly like a correct answer. This site is the twin of the one in
      // `buildVdmConfig`; they were changed apart once and that is what left the defect half-fixed.
      config: ({ platformAccountId, carried }) => ({
        customerId: platformAccountId,
        ...emitCarried(GOOGLE_CARRIES, carried),
      }),
    },
    platformAccountLabel: 'Customer ID',
    mappingAddLabel: 'Add mapping',
    hashesPii: true,
    hashedFields: ['email', 'phone', 'firstName', 'lastName'],
    advancedFields: [
      {
        kind: 'select',
        name: 'userDataConsent',
        label: 'Consent for using this data',
        description: 'What the uploaded users consented to for use of their data',
        default: 'UNSPECIFIED',
        options: GOOGLE_CONSENT_OPTIONS,
      },
      {
        kind: 'select',
        name: 'personalizationConsent',
        label: 'Consent for ad personalisation',
        description: 'What the uploaded users consented to regarding personalised advertising',
        default: 'UNSPECIFIED',
        options: GOOGLE_CONSENT_OPTIONS,
      },
      {
        kind: 'text',
        name: 'appId',
        label: 'App ID',
        description: 'Required for a mobile-advertising-ID list — the app the device ids belong to',
        default: '',
        placeholder: 'e.g. com.example.app',
        visibleWhen: ({ carried }) => carried['type'] === GOOGLE_MOBILE_LIST_TYPE,
      },
    ],
    buildDestinationConfig: ({ accountId, platformAccountId }) => ({
      rudderAccountId: accountId,
      customerId: platformAccountId,
      // `subAccount` / `loginCustomerId` are a manager-account pairing Lookout does not collect: it
      // mints one destination per chosen customer id, so there is no manager indirection to express.
      subAccount: false,
      connectionMode: { warehouse: 'cloud' },
    }),
    buildVdmConfig: (input) => ({
      ...baseVdmConfig(input),
      // Was `typeOfList: input.carried?.['type'] ?? ''`. Now derived from the `carries` declaration,
      // and OMITTED when there is nothing to carry — see `emitCarried` for why the `''` was harmful.
      ...emitCarried(GOOGLE_CARRIES, input.carried),
      userDataConsent: input.advanced['userDataConsent'],
      personalizationConsent: input.advanced['personalizationConsent'],
      // Omitted rather than sent empty: the platform reads its presence as "this is a mobile list".
      ...(input.advanced['appId'] ? { appId: input.advanced['appId'] } : {}),
    }),
    readVdmConfig: (config) => {
      const read = baseReadVdmConfig(config, 'audienceId');
      return {
        ...read,
        carried: readCarried(GOOGLE_CARRIES, config),
        advanced: {
          ...read.advanced,
          // `appId` only when present — its mere presence tells the platform "this is a mobile list",
          // so reading an absent one back as `''` would re-post it and relabel the sync.
          ...readAdvanced(config, ['userDataConsent', 'personalizationConsent', 'appId']),
        },
      };
    },
  },
  HS_AUDIENCE: {
    definitionName: 'HS_AUDIENCE',
    displayName: 'HubSpot Audience',
    platform: 'HubSpot',
    accountDefinitions: ['DESTINATION_HS_AUDIENCE_API_KEY'],
    slug: 'hs_audience',
    // No ad-account layer: the HubSpot Service Key lives on the RudderStack account
    // (`DESTINATION_HS_AUDIENCE_API_KEY`), so the credential alone identifies the portal whose
    // static contact lists we can see. Lookout never handles the key itself.
    platformAccount: { via: 'none' },
    farSide: {
      kind: 'audience',
      path: 'listAudiences',
      configKey: 'audienceId',
      // No account key at all: the credential alone identifies the portal.
      config: () => ({}),
      // Default `{ value, label }` list options — same shape as Reddit / Meta.
      about: 'Lookout lists only static HubSpot contact lists. Active lists do not appear.',
      notice: () =>
        'A HubSpot Record ID is used first. Email is used only when the Record ID is empty, and a bad Record ID does not fall back to email. Deduplicate the source first. Preview shows warehouse rows only.',
      // Empty MANUAL contact list via integrations-info `/setup` (createAudience yes → no + id).
      // Inline name only — HubSpot takes no description. Default existing so create is an explicit
      // choice; the Lookout audience title seeds the name once via `suggest`.
      create: {
        noun: 'list',
        title: 'Select list',
        subtitle: 'Use an existing HubSpot list or create a new one',
        layout: 'inline',
        description: false,
        nameMinLength: 1,
        defaultMode: 'existing',
        suggest: suggestHubSpotListName,
      },
      // Fixed for the life of the sync (HubSpot spec §6.5).
      listFixedOnEdit: true,
      memberNoun: 'contact',
    },
    fields: {
      via: 'endpoint',
      path: 'listFields',
      // HubSpot resolves the portal from the account credential; it needs no far-side id.
      // Create mode has no list id yet; the handler ignores its body and returns static fields.
      config: () => ({}),
    },
    mappingAddLabel: 'Map another field',
    // Matches on HubSpot Record ID / email as plain identifiers — nothing to hash.
    hashesPii: false,
    // Webapp HubSpot connections stay independent (HubSpot spec §2.1).
    neverAdopt: true,
    advancedFields: [],
    buildDestinationConfig: ({ accountId }) => ({
      rudderAccountId: accountId,
      connectionMode: { warehouse: 'cloud' },
    }),
    buildVdmConfig: (input) => baseVdmConfig(input),
    readVdmConfig: (config) => baseReadVdmConfig(config, 'audienceId'),
  },
  ITERABLE_AUDIENCE: {
    definitionName: 'ITERABLE_AUDIENCE',
    displayName: 'Iterable Audience',
    platform: 'Iterable',
    accountDefinitions: ['DESTINATION_ITERABLE_AUDIENCE_API_KEY'],
    slug: 'iterable_audience',
    // No ad-account layer: the Iterable API key, data centre and project type all live on the
    // RudderStack account (`DESTINATION_ITERABLE_AUDIENCE_API_KEY`), so the credential alone
    // identifies the project whose lists we can see. Lookout never handles the key itself.
    platformAccount: { via: 'none' },
    farSide: {
      kind: 'audience',
      path: 'listAudiences',
      configKey: 'audienceId',
      // No account key at all: the credential alone identifies the project.
      config: () => ({}),
      // Iterable is the one destination that does not answer `{ value, label }`.
      optionKeys: { id: 'id', label: 'name' },
      // Standard vs Suppression. The webapp registers this as a hidden form item and persists it on
      // the connection, so a suppression list stays a suppression list.
      carries: ITERABLE_CARRIES,
      // Subscribing to a suppression list does the opposite of what "activate this audience" sounds
      // like, and the list's type is carried rather than chosen — so nothing else on the step tells
      // the user which kind they just picked. The webapp shows the same warning for the same reason.
      notice: (carried) =>
        carried['listType'] === ITERABLE_SUPPRESSION_LIST_TYPE
          ? 'This is a suppression list. Subscribing a user to it EXCLUDES them from the campaigns the list is attached to.'
          : null,
      // `iterableAudience/form.tsx`: name and description sit inline under the choice, with no
      // section heading of their own. A created list carries no `listType` — the webapp resets the
      // hidden field on switching to create — so `audienceListType` is simply omitted.
      create: {
        noun: 'list',
        title: 'Select list',
        subtitle: 'Use an existing Iterable list or create a new one',
        layout: 'inline',
        description: true,
        nameMinLength: 3,
      },
    },
    fields: {
      via: 'endpoint',
      path: 'listFields',
      // Iterable resolves the project from the account credential; it needs no far-side id.
      config: () => ({}),
    },
    mappingAddLabel: 'Map another field',
    // The webapp's Iterable form has no hashing question and its connection config has no such key.
    // Same class as Braze: the question is derived onto every destination, so every destination that
    // does NOT hash has to say so. Found by sweeping the class after fixing Braze.
    hashesPii: false,
    advancedFields: [
      {
        kind: 'select',
        name: 'updateExistingUsersOnly',
        label: 'Update existing users only?',
        description: 'Subscribe only users Iterable already has, rather than creating new ones',
        default: 'no',
        options: YES_NO,
        /**
         * Shown only for a project that HAS a user id to match on. Iterable's own account carries
         * `projectType`, and an `email-based` project can only ever be keyed by email — "update the
         * existing user" is not a distinct outcome there, which is why the webapp renders this field
         * for `hybrid` and `userId-based` alone and omits the key entirely otherwise.
         */
        visibleWhen: ({ accountOptions }) => {
          // Type-checked rather than stringified: `options` is an unschema'd blob, and coercing
          // whatever is under `projectType` would turn an object into `'[object Object]'` and a
          // future non-string into a silent no-match. Only a real string can qualify.
          const projectType = accountOptions['projectType'];
          return typeof projectType === 'string' && ITERABLE_UPDATE_ONLY_PROJECT_TYPES.has(projectType);
        },
      },
    ],
    buildDestinationConfig: ({ accountId }) => ({
      rudderAccountId: accountId,
      connectionMode: { warehouse: 'cloud' },
    }),
    buildVdmConfig: (input) => ({
      ...baseVdmConfig(input),
      // Hidden in the webapp's form but always submitted, and set from the chosen list's own
      // `listType` — so it is carried, never asked for.
      ...emitCarried(ITERABLE_CARRIES, input.carried),
      // Omitted, not sent false, when the project type does not offer the question — matching the
      // webapp, which drops the key entirely rather than writing a default nobody chose. `advanced`
      // only holds it when the field was visible, so the presence check IS the project-type check.
      ...(input.advanced['updateExistingUsersOnly'] !== undefined
        ? { updateExistingUsersOnly: input.advanced['updateExistingUsersOnly'] === 'yes' }
        : {}),
    }),
    readVdmConfig: (config) => {
      const read = baseReadVdmConfig(config, 'audienceId');
      // Back to the 'yes'/'no' the select speaks. Absent stays absent: an edit of a sync made on an
      // email-based project must not acquire the key just by being opened.
      const updateExistingUsersOnly = config['updateExistingUsersOnly'];
      return {
        ...read,
        carried: readCarried(ITERABLE_CARRIES, config),
        advanced: {
          ...read.advanced,
          ...(typeof updateExistingUsersOnly === 'boolean'
            ? { updateExistingUsersOnly: updateExistingUsersOnly ? 'yes' : 'no' }
            : {}),
        },
      };
    },
    // Nothing to adopt — an Iterable destination carries no platform account id at all.
  },
  LINKEDIN_AUDIENCE: {
    definitionName: 'LINKEDIN_AUDIENCE',
    displayName: 'LinkedIn Audience',
    platform: 'LinkedIn Ads',
    accountDefinitions: ['DESTINATION_LINKEDIN_AUDIENCE_OAUTH'],
    slug: 'linkedin_audience',
    // Not `destinationData`: LinkedIn answers on its own `listAccounts`, with the same body shape as
    // the audience list. And the chosen id is persisted on the SYNC, not the destination — which is
    // why `adoptPlatformAccountId` reads the sync config rather than the destination's.
    platformAccount: {
      via: 'listAccounts',
      path: 'listAccounts',
      configKey: 'accountId',
      // The destination config carries no account id for this type; the sync's does.
      carriedIn: 'vdm',
    },
    audienceTypes: {
      label: 'Audience type',
      options: [
        { value: 'user', label: 'User' },
        { value: 'company', label: 'Company' },
      ],
      // The webapp leaves this unset until the user picks; defaulting to `user` instead means the
      // audience list can load as soon as an ad account is chosen, and `user` is the arm whose
      // identifier table (hashed email) is by far the more common.
      /**
       * Load-bearing, not a convenience. `listAudiences` REQUIRES `audienceType` in its config and
       * fails silently without it — verified against a real LinkedIn credential:
       *
       *   accountId + audienceType=user     -> 200, 8 audiences
       *   accountId + audienceType=company  -> 200, 2 audiences
       *   accountId alone                   -> 200, EMPTY list (not an error)
       *
       * So an absent default renders an empty picker rather than a visible failure, which is the
       * hardest shape of bug to attribute. Recorded here because "no default" reads as harmless.
       */
      default: 'user',
    },
    farSide: {
      kind: 'audience',
      path: 'listAudiences',
      configKey: 'audienceId',
      // Scoped by BOTH the ad account and the audience type — a `company` audience is a different
      // list from a `user` one on the same account. `createAudience` rides along because the webapp
      // sends it and the endpoint scopes on it; the LIST is only ever asked for on the attach path,
      // so it is always 'no' here.
      config: ({ platformAccountId, audienceType }) => ({
        createAudience: 'no',
        accountId: platformAccountId,
        // A conditional spread, not `?? undefined`: this returns Record<string, string>, where
        // `undefined` is not a legal value — the key must be absent rather than present-and-empty.
        ...(audienceType !== undefined ? { audienceType } : {}),
      }),
      // `linkedInAudience/form.tsx`. `/setup` creates a DMP segment on the sync's `accountId`, of the
      // chosen `audienceType` — both already in the vdm config this spec builds.
      create: {
        noun: 'audience',
        title: 'Select audience',
        subtitle: USE_EXISTING_OR_CREATE_AUDIENCE,
        layout: 'section',
        description: true,
        nameMinLength: 3,
      },
    },
    // Static, not fetched: the webapp hardcodes these four tables client-side and never asks the
    // platform. There is no `listFields` for this destination to ask.
    fields: {
      via: 'static',
      identifiers: ({ audienceType }) =>
        audienceType === 'company' ? LINKEDIN_COMPANY_IDENTIFIERS : LINKEDIN_USER_IDENTIFIERS,
      extras: ({ audienceType }) => (audienceType === 'company' ? LINKEDIN_COMPANY_EXTRAS : LINKEDIN_USER_EXTRAS),
    },
    platformAccountLabel: 'Ad Account',
    mappingAddLabel: 'Map another field',
    hashesPii: true,
    hashedFields: ['sha256Email', 'sha512Email'],
    /**
     * Asked for a `user` audience only, matching the webapp, which wraps the whole Advanced
     * collapse in `typeOfAudience === 'user'`. A company audience matches on company name, domain
     * and URN — none of them PII, none of them hashed — so the question has no answer that means
     * anything.
     *
     * The KEY is still written either way; see `buildVdmConfig`. Hiding a field drops its value from
     * the submitted bag (that is what stops a stale Google `appId` riding along), and LinkedIn's
     * transformer schema requires `isHashRequired` as a non-optional boolean — so hiding alone would
     * have made every company audience fail router-input validation.
     */
    hashesPiiWhen: ({ audienceType }) => audienceType !== 'company',
    advancedFields: [],
    extraMappings: {
      label: 'Map attributes',
      detailLabel: 'Attributes',
      description: 'Optional — extra fields sent alongside the identity, not used to match on',
      addLabel: 'Map another field',
      rowNoun: 'attribute mapping',
    },
    // Only the credential. `accountId` is NOT here — it belongs to the sync (see `platformAccount`).
    buildDestinationConfig: ({ accountId }) => ({
      rudderAccountId: accountId,
      connectionMode: { warehouse: 'cloud' },
    }),
    buildVdmConfig: (input) => ({
      ...baseVdmConfig(input),
      // Restated because a company audience is never ASKED (see `hashesPiiWhen`) and so carries no
      // answer in `advanced` — but the key is mandatory in the transformer's router-input schema,
      // and the webapp submits `true` there regardless: its Ant form keeps the unmounted field's
      // initial value. `true` is also the honest answer, since LinkedIn's own company identifiers
      // are not hashed and the flag is a no-op for them.
      isHashRequired:
        input.advanced['isHashRequired'] !== undefined ? input.advanced['isHashRequired'] === 'yes' : true,
      accountId: input.platformAccountId,
      audienceType: input.audienceType ?? undefined,
      fieldMappings: input.fieldMappings,
    }),
    readVdmConfig: (config) => {
      const read = baseReadVdmConfig(config, 'audienceId');
      // `accountId` is NOT read back here even though LinkedIn writes it into this very blob —
      // `adoptPlatformAccountId` owns that read for all seven, off the `carriedIn` discriminant. See
      // the note on `VdmConfigRead`.
      //
      // `audienceType` has no column and exists nowhere else, so this is its only recovery path. If
      // it comes back absent the audience picker renders EMPTY rather than erroring — `listAudiences`
      // answers 200 with an empty list when the axis is missing — so an absent value here must be
      // treated as a failed read by the caller, not as "the user hasn't chosen yet".
      const audienceType = configString(config, 'audienceType');
      // A company audience is never ASKED about hashing, so it must not be told it answered. The
      // builder writes the key regardless (the transformer requires it), which would otherwise come
      // back as a settled advanced value and reappear in the panel the moment the field is shown
      // again. Dropping it here keeps read and write the exact inverse of each other: asked ⇒
      // round-trips, not asked ⇒ absent on both sides.
      const advanced = { ...read.advanced };
      if (audienceType === 'company') delete advanced['isHashRequired'];
      return {
        ...read,
        advanced,
        fieldMappings: readMappings(config, 'fieldMappings'),
        ...(audienceType !== null ? { audienceType } : {}),
      };
    },
  },
  MARKETO_AUDIENCE: {
    definitionName: 'MARKETO_AUDIENCE',
    displayName: 'Marketo Audience',
    platform: 'Marketo',
    accountDefinitions: ['DESTINATION_MARKETO_AUDIENCE_API_KEY'],
    slug: 'marketo_audience',
    // No second account level: the Munchkin ID on the RudderStack account
    // (`DESTINATION_MARKETO_AUDIENCE_API_KEY`) identifies the instance, and its client ID and secret
    // stay there too. Lookout never handles them. Not the legacy `MARKETO_STATIC_LIST`, which has no
    // account definition and stays unregistered.
    platformAccount: { via: 'none' },
    farSide: {
      kind: 'audience',
      path: 'listAudiences',
      configKey: 'audienceId',
      // No account key at all: the credential alone identifies the instance.
      config: () => ({}),
      // Default `{ value, label }` list options.
      about: 'Lookout lists only Marketo static lists. Smart lists do not appear.',
      notice: () =>
        'Map the Marketo Lead ID if you have it: it is used first, and an invalid Lead ID fails the row without falling back to email. Email is used only when the Lead ID is empty; an email that matches more than one Marketo lead fails until the duplicates are merged in Marketo.',
      // No `create`: existing static lists only (Marketo spec D3) — list creation needs a folder and
      // the Read-Write Asset permission. So the noun is declared here instead.
      noun: 'list',
      // Fixed for the life of the sync, as HubSpot (Marketo spec §6.5).
      listFixedOnEdit: true,
      memberNoun: 'lead',
      // An instance can hold tens of thousands of static lists; search and page on the server.
      listSearch: 'server',
    },
    fields: {
      via: 'endpoint',
      path: 'listFields',
      // Marketo Lead ID and email, whichever list is chosen; the handler needs no far-side id.
      config: () => ({}),
    },
    mappingAddLabel: 'Map another field',
    // Matches on Marketo Lead ID / email as plain identifiers — nothing to hash.
    hashesPii: false,
    // Client ownership is the same as HubSpot (Marketo spec §2.1): webapp connections stay independent.
    neverAdopt: true,
    advancedFields: [],
    buildDestinationConfig: ({ accountId }) => ({
      rudderAccountId: accountId,
      connectionMode: { warehouse: 'cloud' },
    }),
    buildVdmConfig: (input) => baseVdmConfig(input),
    readVdmConfig: (config) => baseReadVdmConfig(config, 'audienceId'),
  },
  PINTEREST_AUDIENCE: {
    definitionName: 'PINTEREST_AUDIENCE',
    displayName: 'Pinterest Audience',
    platform: 'Pinterest',
    accountDefinitions: ['DESTINATION_PINTEREST_AUDIENCE_OAUTH'],
    slug: 'pinterest_audience',
    // Reddit's shape (ACT2-1195, spec §8.2). Discovery is integrations-info's own `/listAdAccounts`
    // options route on the shared audience router; the id is Pinterest's numeric ad-account id. Written
    // to both blobs and read sync-first for the same reason as Reddit: the webapp sync form stores it on
    // the sync, an agentic writer may set it on the destination, and `/setup` resolves it body → vdm →
    // config. `/setup` needs it on BOTH paths here (it reads the audience to find its customer list, or
    // creates the list and audience in it).
    platformAccount: {
      via: 'listAccounts',
      path: 'listAdAccounts',
      configKey: 'adAccountId',
      carriedIn: 'vdm-or-destination',
    },
    farSide: {
      kind: 'audience',
      path: 'listAudiences',
      configKey: 'audienceId',
      config: ({ platformAccountId }) => ({ adAccountId: platformAccountId }),
      // integrations-info keeps CUSTOMER_LIST audiences only (an account with only engagement or
      // lookalike audiences sees an empty list), and Pinterest holds an audience at TOO_SMALL below 100
      // matched Pinners and takes 48–72 h to process it (spec §3.3). Static and true of every sync: `about`.
      about:
        "Only Customer list audiences are listed. New audiences need at least 100 matched Pinterest users and take 48–72 hours to become ready. Don't send IDFA collected from iOS app or website activity — Pinterest's policy forbids it.",
      // `/setup` with createAudience 'yes' creates a customer list and a CUSTOMER_LIST audience on it in
      // the sync's ad account; integrations-info refuses names outside 3–500 characters (spec §6) and
      // Pinterest's create call takes no description.
      create: {
        noun: 'audience',
        title: 'Select audience',
        subtitle: 'Use an existing Pinterest audience or create a new one',
        layout: 'inline',
        description: false,
        nameMinLength: 3,
        nameMaxLength: 500,
      },
    },
    fields: {
      via: 'endpoint',
      path: 'listFields',
      // Static EMAIL / MAID; the handler ignores its body.
      config: () => ({}),
    },
    platformAccountLabel: 'Ad Account',
    mappingAddLabel: 'Map another field',
    // The transformer normalises (spec §3.3) and SHA-256s raw values unless `isHashRequired` is false,
    // in which case it rejects anything that is not 64-hex.
    hashesPii: true,
    hashedFields: ['EMAIL', 'MAID'],
    hashesPiiDescription:
      'RudderStack will SHA-256 hash the mapped email addresses and mobile advertising IDs before syncing to Pinterest. Choose No only when your columns already hold SHA-256 hashes.',
    advancedFields: [],
    // integrations-info's routes are written for a person ("Only Customer list audiences can be synced";
    // a half-finished create names the customer list it made), so a refusal is shown as written.
    setupRefusalIsUserFacing: true,
    buildDestinationConfig: ({ accountId, platformAccountId }) => ({
      rudderAccountId: accountId,
      adAccountId: platformAccountId,
      connectionMode: { warehouse: 'cloud' },
      authStatus: 'active',
    }),
    // `isHashRequired` must be a boolean (transformer connection schema, spec §7.1); the base builder
    // writes one. `customerListId` is never written here: `/setup` mints it and the save merges it in.
    buildVdmConfig: (input) => ({
      ...baseVdmConfig(input),
      adAccountId: input.platformAccountId,
    }),
    readVdmConfig: (config) => baseReadVdmConfig(config, 'audienceId'),
  },
  REDDIT_AUDIENCE: {
    definitionName: 'REDDIT_AUDIENCE',
    displayName: 'Reddit Audience',
    platform: 'Reddit Ads',
    accountDefinitions: ['DESTINATION_REDDIT_AUDIENCE_OAUTH'],
    slug: 'reddit_audience',
    // Reddit's ad-account discovery is its own `listAdAccounts` route bolted onto the shared audience
    // router (rudder-integrations-info `reddit_audience/routes.ts`), answering an options list from
    // the OAuth identity — the LinkedIn shape, not the `destinationData` one FBCA and TikTok use. The
    // id is the API's `a2_`-prefixed form; Reddit Ads Manager shows a different `ads_` id that the
    // API rejects, which is why the webapp made this picker-only and never free text.
    //
    // The chosen id is WRITTEN to both blobs and READ from the sync first, then the destination.
    // integrations-info's `resolveAdAccountId` (`adAccountId` → `vdmConfig.adAccountId` →
    // `config.adAccountId`) is not the same order for the flat bodies config-backend sends: the
    // middleware lifts the destination's `config.adAccountId` to the top level, so the destination
    // wins there. Lookout never depends on that order, since it writes the same id to both blobs and
    // calls `listAudiences` with no destinationId. The two writers disagree: the webapp's
    // destination form collects only the credential (`ui-config.json` has one
    // `accountManagementInput`) and its sync form spreads the pick into `config.destination`, while
    // the agentic/MCP path sets it on the destination (the definition's `mappingGuidance` and
    // `contextFields`) and `setup` with `createAudience: 'no'` never copies it onto the sync. Reading
    // one blob only would leave one writer's syncs unadoptable. Lookout writes both so that every
    // reader finds it: the webapp's edit form and Lookout's own adoption read the sync's, the
    // webapp's fallback and the agentic surface read the destination's. Reddit's `setup` itself
    // needs neither on the attach path — it resolves the ad account only when creating an audience
    // (`createAudience: 'yes'`, ACT2-1082), where it reads the one the vdm config carries. Delivery
    // never reads it: the transformer PATCHes the audience by id.
    platformAccount: {
      via: 'listAccounts',
      path: 'listAdAccounts',
      configKey: 'adAccountId',
      carriedIn: 'vdm-or-destination',
    },
    farSide: {
      kind: 'audience',
      path: 'listAudiences',
      configKey: 'audienceId',
      config: ({ platformAccountId }) => ({ adAccountId: platformAccountId }),
      // The webapp form opens with the match requirement, and integrations-info lists only
      // CUSTOMER_LIST audiences (the other five Reddit types cannot take uploaded users), so a user
      // whose account holds only lookalike or engagement audiences sees an empty list. Say both up
      // front rather than leave "No audiences found" unexplained. Static and true of every sync, so
      // it is `about` (info), not `notice` (a warning about the chosen audience).
      about:
        'Only Reddit Customer List audiences are listed. Reddit needs at least 1,000 matched users before it marks an audience as valid, and can take up to 4 hours to process an upload.',
      // `redditAudience/form.tsx`: name only (Reddit's create call takes no description), inline, and
      // the one destination with an upper bound — integrations-info refuses outside 3–500 characters.
      // `/setup` creates a CUSTOMER_LIST audience in the sync's `adAccountId`.
      create: {
        noun: 'audience',
        title: 'Select audience',
        subtitle: 'Use an existing Reddit custom audience or create a new one',
        layout: 'inline',
        description: false,
        nameMinLength: 3,
        nameMaxLength: 500,
      },
    },
    fields: {
      via: 'endpoint',
      path: 'listFields',
      // Reddit's `listFields` is static — exactly EMAIL_SHA256 and MAID_SHA256, the only two columns
      // its Custom Audiences endpoint accepts — and the handler ignores its body. New here, so no
      // shipped request to preserve: send nothing, as Iterable does.
      config: () => ({}),
    },
    platformAccountLabel: 'Ad Account',
    mappingAddLabel: 'Map another field',
    mappingHint: 'Reddit matches on email and mobile ad ID only. Map both if you have them.',
    // The transformer canonicalises (lowercase, strip +alias) and SHA-256s raw values on send. The
    // suffix on each field NAMES the hash it applies; it is not an instruction to pre-hash. `no` is
    // for a warehouse that already holds 64-hex SHA-256 values.
    hashesPii: true,
    hashedFields: ['EMAIL_SHA256', 'MAID_SHA256'],
    // The shared copy says "emails and phone numbers"; Reddit Custom Audiences take no phone number
    // at all (email and MAID only), so the shared sentence would be false here.
    hashesPiiDescription:
      'RudderStack will SHA-256 hash the mapped email addresses and mobile advertising IDs before syncing to Reddit Ads. Choose No only when your columns already hold SHA-256 hashes.',
    advancedFields: [],
    buildDestinationConfig: ({ accountId, platformAccountId }) => ({
      rudderAccountId: accountId,
      adAccountId: platformAccountId,
      connectionMode: { warehouse: 'cloud' },
      authStatus: 'active',
    }),
    // Reddit's connection schema requires `isHashRequired` as a BOOLEAN (transformer
    // `RedditAudienceConnectionConfigSchema`); the base builder writes one. `adAccountId` is restated
    // on the sync because that is where the webapp stores it and where adoption reads first (above).
    buildVdmConfig: (input) => ({
      ...baseVdmConfig(input),
      adAccountId: input.platformAccountId,
    }),
    readVdmConfig: (config) => baseReadVdmConfig(config, 'audienceId'),
  },
  TIKTOK_AUDIENCE: {
    definitionName: 'TIKTOK_AUDIENCE',
    displayName: 'TikTok Audience',
    platform: 'TikTok Ads',
    accountDefinitions: ['DESTINATION_TIKTOK_AUDIENCE_OAUTH'],
    slug: 'tiktok_audience',
    platformAccount: {
      via: 'destinationData',
      destApi: 'listAdvertisers',
      configKey: 'advertiserId',
    },
    farSide: {
      kind: 'audience',
      path: 'listAudiences',
      configKey: 'audienceId',
      config: ({ platformAccountId }) => ({ advertiserId: platformAccountId }),
      // `tiktokAudience/form.tsx`. `/setup` creates a segment audience under the destination's
      // `advertiserId`; TikTok's create call takes the name only, so a description is sent (as the
      // webapp sends it) and ignored there.
      create: {
        noun: 'audience',
        title: 'Select audience',
        subtitle: USE_EXISTING_OR_CREATE_AUDIENCE,
        layout: 'section',
        description: true,
        nameMinLength: 3,
      },
    },
    fields: {
      via: 'endpoint',
      path: 'listFields',
      // Sends the far-side id, same as FBCA — because that is what the pre-registry code sent for
      // BOTH types, unconditionally. An inherited comment said "TikTok ignores config", but the old
      // code never relied on it: it always sent the id, so the claim was never exercised against the
      // live endpoint. Dropping it here would have made a refactor that promises no behaviour change
      // quietly alter a shipped request body. Narrow this only with a recorded manual TikTok pass.
      //
      // Create mode has no id yet, so the key is omitted then rather than sent as `''`. That is safe
      // by the endpoint's own source: integrations-info `tiktok_audience/routes.ts` `listFieldsHandler`
      // ignores its body and returns the static `supportedFields`.
      config: ({ farSideAudienceId }): Record<string, string> =>
        farSideAudienceId.length > 0 ? { audienceId: farSideAudienceId } : {},
    },
    platformAccountLabel: 'Advertiser Id',
    mappingAddLabel: 'Map another field',
    mappingHint:
      'Map every identifier you have, as raw values. The sync hashes them when "Automatically hash PII data" is on.',
    hashesPii: true,
    hashedFields: ['EMAIL_SHA256', 'PHONE_SHA256', 'IDFA_SHA256', 'AAID_SHA256', 'IDFA_MD5', 'AAID_MD5'],
    advancedFields: [],
    buildDestinationConfig: ({ accountId, platformAccountId }) => ({
      rudderAccountId: accountId,
      advertiserId: platformAccountId,
      connectionMode: { warehouse: 'cloud' },
      authStatus: 'active',
    }),
    buildVdmConfig: (input) => baseVdmConfig(input),
    readVdmConfig: (config) => baseReadVdmConfig(config, 'audienceId'),
  },
} as const satisfies Record<string, ActivationDestinationSpec>;

/** Every Activate destination type — derived, so a new spec entry widens it automatically. */
export type ActivationDestinationType = keyof typeof ACTIVATION_DESTINATION_SPECS;

/**
 * Ordered list of the type names. `Object.keys` on a `const` object is insertion-ordered, so this
 * is also the order the wizard's type list renders in.
 */
export const ACTIVATION_DESTINATION_TYPE_NAMES = Object.keys(
  ACTIVATION_DESTINATION_SPECS,
) as readonly ActivationDestinationType[] as [ActivationDestinationType, ...ActivationDestinationType[]];

/**
 * Every RS account-definition name the registry names, across every spec — the full set of account
 * definitions Lookout may ever offer or select. Derived from `ACTIVATION_DESTINATION_SPECS`, the same
 * way `ACTIVATION_DESTINATION_TYPE_NAMES` is: adding a destination (or widening an existing one's
 * `accountDefinitions`) grows this automatically, so nothing here is ever hand-listed. This is the
 * scope the account-management service layer defaults to and clamps against — see
 * `services/rudderstack-accounts.ts` (rudder-specs scope-reduction addendum, "the rule in one line").
 */
export const ACTIVATION_ACCOUNT_DEFINITION_NAMES: readonly string[] = [
  ...new Set(ACTIVATION_DESTINATION_TYPE_NAMES.flatMap((t) => ACTIVATION_DESTINATION_SPECS[t].accountDefinitions)),
];

/**
 * Every account-definition name Lookout will still MANAGE: the selectable set above plus each spec's
 * `legacyAccountDefinitions` — names a destination recognises but Lookout no longer offers.
 *
 * The split exists because the two questions have different answers. Narrowing what a user may
 * SELECT is a product decision Lookout gets to make on its own; narrowing what it will MANAGE strands
 * accounts that already exist in a customer's workspace, whose rows then vanish from Settings while
 * their destinations keep delivering. So the account-management reads take an explicit scope
 * (`services/rudderstack-accounts.ts`, `RsAccountScope`) rather than inferring one: the picker asks
 * for `'select'`, Settings asks for `'manage'`, and the default is `'select'` so a caller that says
 * nothing stays locked rather than silently widening.
 *
 * Derived the same way as the selectable set — `flatMap` over the specs, never hand-listed — so
 * retiring a grant is one spec edit and both sets follow.
 */
export const ACTIVATION_MANAGEABLE_ACCOUNT_DEFINITION_NAMES: readonly string[] = [
  ...new Set(
    ACTIVATION_DESTINATION_TYPE_NAMES.flatMap((t) => [
      ...activationSpec(t).accountDefinitions,
      ...(activationSpec(t).legacyAccountDefinitions ?? []),
    ]),
  ),
];

/** The spec for a known type. Callers hold an `ActivationDestinationType`, so this cannot miss. */
export function activationSpec(type: ActivationDestinationType): ActivationDestinationSpec {
  return ACTIVATION_DESTINATION_SPECS[type];
}

/**
 * Narrow an arbitrary `definitionName` — one a RudderStack destination reported — to a type we know.
 * Returns `null` for everything else, which is the adoption path's "not one of ours" answer rather
 * than an error: the workspace's sql-audience sources include destinations built in the webapp.
 */
export function activationDestinationTypeOf(definitionName: string): ActivationDestinationType | null {
  return Object.hasOwn(ACTIVATION_DESTINATION_SPECS, definitionName)
    ? (definitionName as ActivationDestinationType)
    : null;
}

/**
 * Apply the spec's normalisation to a platform account id (trims even when the spec has no rule).
 * A type with no platform account normalises to the empty string whatever it is handed — there is
 * no id to carry, so a stray one from an older client is dropped rather than stored.
 */
export function normalizePlatformAccountId(type: ActivationDestinationType, raw: string): string {
  const { platformAccount } = activationSpec(type);
  if (platformAccount.via === 'none') return '';
  return platformAccount.normalize ? platformAccount.normalize(raw) : raw.trim();
}

/**
 * Recover the platform account id from a destination Lookout did not mint, for `adoptActivation`.
 *
 * Derived rather than a per-spec callback: every destination stores the id under its own
 * `configKey`, in the blob its `carriedIn` names, so four hand-written readers were four chances to
 * disagree with the key the write path used. `null` means "cannot be adopted", which is a
 * legitimate outcome — `listSqlAudiences` returns webapp-authored sources too.
 */
export function adoptPlatformAccountId(
  type: ActivationDestinationType,
  destinationConfig: Record<string, unknown>,
  syncDestinationConfig: Record<string, unknown>,
): string | null {
  const { platformAccount } = activationSpec(type);
  if (platformAccount.via === 'none') return null;
  const { configKey, carriedIn } = platformAccount;
  if (carriedIn === 'vdm-or-destination') {
    return configString(syncDestinationConfig, configKey) ?? configString(destinationConfig, configKey);
  }
  return configString(carriedIn === 'vdm' ? syncDestinationConfig : destinationConfig, configKey);
}

/**
 * Whether an unrecorded connection of this type, on a source a Lookout audience owns, may be adopted
 * into `audience_activations` (`neverAdopt` — HubSpot, Marketo — says no).
 */
export function adoptsUnrecordedConnections(type: ActivationDestinationType): boolean {
  return activationSpec(type).neverAdopt !== true;
}

/** Whether the wizard must collect a platform account before it can list audiences. */
export function requiresPlatformAccount(type: ActivationDestinationType): boolean {
  return activationSpec(type).platformAccount.via !== 'none';
}

/**
 * The `config` for the far-side audience list. Delegates to the spec, because the scoping is
 * per-destination: an ad account for FBCA/TikTok, an ad account AND an audience type for LinkedIn,
 * nothing at all for Iterable.
 */
export function audienceListConfig(
  type: ActivationDestinationType,
  selection: ActivationSelection,
): Record<string, string> {
  const { farSide } = activationSpec(type);
  // An attribute-kind destination has no list to scope; the caller should not be asking.
  return farSide.kind === 'audience' ? farSide.config(selection) : {};
}

/** The far-side spec, for a caller that must branch on which shape this destination has. */
export function farSideSpec(type: ActivationDestinationType): FarSideSpec {
  return activationSpec(type).farSide;
}

/**
 * Read the far-side target back out of a sync's own `config.destination`.
 *
 * Keyed by the spec rather than a hardcoded `audienceId`: Braze stores a `customAttributeName`
 * there, and adoption has to find it under the same key the write path used.
 */
/**
 * Why the chosen far-side value is not acceptable, or `null` if it is.
 *
 * One function so the wizard's message, the wizard's Next gate and the server's Enable check are
 * literally the same rule. They were not: the spec's own comment claimed the pattern was "enforced
 * in the wizard and again on Enable", while the wizard only COLOURED the field red and the server
 * checked non-empty — so a malformed Braze attribute name sailed through four steps and failed on a
 * toast at the end, or reached `customAttributeName` unchecked.
 *
 * Returns a message meant for a person, since all three callers show it to one.
 */
export function farSideAudienceIdError(type: ActivationDestinationType, value: string): string | null {
  const spec = activationSpec(type);
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return spec.farSide.kind === 'attribute' ? `${spec.farSide.label} is required` : 'Pick an audience to sync into';
  }
  if (spec.farSide.kind !== 'attribute') return null;
  if (trimmed.length > spec.farSide.maxLength) {
    return `Keep this to ${spec.farSide.maxLength} characters or fewer`;
  }
  if (!spec.farSide.pattern.test(trimmed)) return 'Use letters, numbers and underscores only';
  return null;
}

/** Whether the far-side target is fixed once the activation exists (`farSide.lockedAfterCreate`). */
export function farSideLockedAfterCreate(type: ActivationDestinationType): boolean {
  const { farSide } = activationSpec(type);
  return farSide.kind === 'attribute' && farSide.lockedAfterCreate === true;
}

/**
 * Whether the far-side list is fixed once the activation exists (`farSide.listFixedOnEdit` —
 * HubSpot, Marketo). Read by the edit sheet to lock the picker and by the service to refuse a change.
 */
export function farSideListFixedOnEdit(type: ActivationDestinationType): boolean {
  const { farSide } = activationSpec(type);
  return farSide.kind === 'audience' && farSide.listFixedOnEdit === true;
}

/** True when the far-side picker searches and pages on the server — see `FarSideSpec.listSearch`. */
export function farSideSearchesOnServer(type: ActivationDestinationType): boolean {
  const { farSide } = activationSpec(type);
  return farSide.kind === 'audience' && farSide.listSearch === 'server';
}

/**
 * What the picker calls the far-side object: the create block's noun when there is one, else the far
 * side's own, else "audience".
 */
export function farSideNoun(type: ActivationDestinationType): FarSideCreateSpec['noun'] {
  const { farSide } = activationSpec(type);
  if (farSide.kind !== 'audience') return 'audience';
  return farSide.create?.noun ?? farSide.noun ?? 'audience';
}

/** The create declaration for a destination, or `null` when it can only attach to an existing one. */
export function farSideCreateSpec(type: ActivationDestinationType): FarSideCreateSpec | null {
  const { farSide } = activationSpec(type);
  return farSide.kind === 'audience' ? (farSide.create ?? null) : null;
}

/** Every string the Create / Use existing UI shows, derived from the spec's noun the webapp's way. */
export type FarSideCreateCopy = {
  createOption: string;
  existingOption: string;
  choiceRequired: string;
  detailsTitle: string;
  detailsSubtitle: string;
  namePlaceholder: string;
  nameRequired: string;
  nameTooShort: string;
  nameTooLong: string;
  descriptionPlaceholder: string;
  descriptionTooLong: string;
};

/**
 * The webapp forms spell these per destination, but every one is the same template over its noun —
 * "Create new list" / "Please enter list name" for Google and Iterable, "audience" for the rest
 * (`audienceCreationOptions` / `userListCreationOptions` and the form rules in each `form.tsx`).
 * Deriving them keeps a new destination from inventing a sixth spelling.
 */
export function farSideCreateCopy(create: FarSideCreateSpec): FarSideCreateCopy {
  const { noun } = create;
  const Noun = noun === 'list' ? 'List' : 'Audience';
  return {
    createOption: `Create new ${noun}`,
    existingOption: `Use existing ${noun}`,
    choiceRequired: noun === 'list' ? 'Please select or create a list' : 'Please select or create an audience',
    detailsTitle: `Specify new ${noun} details`,
    detailsSubtitle: `Enter ${noun} name and description`,
    namePlaceholder: `${Noun} name`,
    nameRequired: `Please enter ${noun} name`,
    nameTooShort: `${Noun} name must be at least ${create.nameMinLength} characters`,
    nameTooLong: `${Noun} name must be at most ${farSideCreateNameMaxLength(create)} characters`,
    descriptionPlaceholder: 'Enter description',
    descriptionTooLong: `Description must be at most ${FAR_SIDE_CREATE_DESCRIPTION_MAX_LENGTH} characters`,
  };
}

/** The name ceiling for a destination: its own (Reddit's 500), else Lookout's shared one. */
export function farSideCreateNameMaxLength(create: FarSideCreateSpec): number {
  return create.nameMaxLength ?? FAR_SIDE_CREATE_NAME_MAX_LENGTH;
}

/**
 * Why a new far-side audience's NAME is not acceptable, or `null` if it is. The webapp's rules
 * (required, min 3, Reddit's max 500), measured on the trimmed name — the trimmed name is what is sent.
 */
export function farSideCreateNameError(type: ActivationDestinationType, name: string): string | null {
  const create = farSideCreateSpec(type);
  if (create === null) return null;
  const copy = farSideCreateCopy(create);
  const trimmed = name.trim();
  if (trimmed.length === 0) return copy.nameRequired;
  if (trimmed.length < create.nameMinLength) return copy.nameTooShort;
  if (trimmed.length > farSideCreateNameMaxLength(create)) return copy.nameTooLong;
  return null;
}

/** Why a new far-side audience's DESCRIPTION is not acceptable, or `null` if it is (trimmed). */
export function farSideCreateDescriptionError(type: ActivationDestinationType, description: string): string | null {
  const create = farSideCreateSpec(type);
  if (create === null) return null;
  return description.trim().length > FAR_SIDE_CREATE_DESCRIPTION_MAX_LENGTH
    ? farSideCreateCopy(create).descriptionTooLong
    : null;
}

/**
 * The create-mode fields this selection makes required ({@link FarSideCreateSpec} `requiredFields`
 * whose Advanced field is currently visible) — Google's App ID, for a mobile-advertising-id list.
 */
export function visibleCreateRequiredFields(
  type: ActivationDestinationType,
  context: AdvancedVisibility,
): readonly FarSideCreateRequiredField[] {
  const create = farSideCreateSpec(type);
  if (create?.requiredFields === undefined) return [];
  const visible = new Set(visibleAdvancedFields(type, context).map((field) => field.name));
  return create.requiredFields.filter((field) => visible.has(field.name));
}

/**
 * Why a create-mode selection cannot be enabled, or `null` if it can — one function for the wizard's
 * Next gate and the server's Enable check, as {@link farSideAudienceIdError} is for the attach path.
 *
 * `context.carried` must already hold the chosen list type for a destination that asks one.
 */
export function farSideCreateError(
  type: ActivationDestinationType,
  input: FarSideCreateInput,
  context: AdvancedVisibility & { advanced: Record<string, string> },
): string | null {
  const create = farSideCreateSpec(type);
  if (create === null) return `${activationSpec(type).displayName} cannot create a new audience from Lookout`;
  const nameError = farSideCreateNameError(type, input.name);
  if (nameError !== null) return nameError;
  if (!create.description && input.description !== undefined && input.description.trim().length > 0) {
    return `${activationSpec(type).platform} does not take a description for a new ${create.noun}`;
  }
  const descriptionError = farSideCreateDescriptionError(type, input.description ?? '');
  if (descriptionError !== null) return descriptionError;
  if (create.listType !== undefined) {
    const chosen = context.carried[create.listType.carriedKey];
    if (chosen === undefined || !create.listType.options.some((option) => option.value === chosen)) {
      return `${create.listType.label} must be one of the offered options`;
    }
  }
  for (const field of visibleCreateRequiredFields(type, context)) {
    if ((context.advanced[field.name] ?? '').trim().length === 0) return field.requiredMessage;
  }
  return null;
}

/**
 * `/setup`'s answer to a CREATE, turned into the connection config Lookout saves — or `null` when it
 * carries no usable new id.
 *
 * Every integrations-info handler but one answers `{ ...vdmConfig minus audience, createAudience:
 * 'no', audienceId }`. LinkedIn answers `{ audienceId, ...vdmConfig }`, so `createAudience: 'yes'` and
 * the `audience` object come back too. Saved as-is, that config says "create on every edit": the
 * webapp papers over it by forcing `createAudience: 'no'` in its EDIT form, and Lookout's editor
 * refuses a `'yes'` outright (`ActivationNotEditableError`). Normalising once, here, before the
 * connection exists, is the Lookout equivalent of the webapp's edit-time override — and it leaves the
 * stored config exactly what an attach-path sync to the same audience would hold.
 *
 * The id must be a non-empty string. Reddit and Iterable already refuse to answer without one; the
 * others would hand back `undefined`, and a connection saved without an id has nothing to sync into.
 */
export function normalizeCreatedSetupData(
  data: Record<string, unknown>,
): { config: Record<string, unknown>; audienceId: string } | null {
  // TikTok passes the vendor's `audience_id` through untouched; its API documents a string, but a
  // numeric id is still a real id, so it is stringified rather than treated as a failed create.
  const raw = data['audienceId'];
  const audienceId = typeof raw === 'number' && Number.isFinite(raw) ? String(raw) : raw;
  if (typeof audienceId !== 'string' || audienceId.trim().length === 0) return null;
  const { audience: _created, ...rest } = data;
  return { config: { ...rest, createAudience: 'no', audienceId }, audienceId };
}

export function readFarSideId(
  type: ActivationDestinationType,
  syncDestinationConfig: Record<string, unknown>,
): string | null {
  return configString(syncDestinationConfig, activationSpec(type).farSide.configKey);
}

/**
 * The destination-side fields a source column can be mapped to, for both mapping tables.
 *
 * Returns `null` when the spec fetches its fields from an endpoint — the caller must make that call.
 * A non-null answer means the list is known locally and no request is needed, which is what lets the
 * service skip an HTTP round-trip for LinkedIn rather than calling a route that does not exist.
 */
export function staticActivationFields(
  type: ActivationDestinationType,
  selection: ActivationSelection,
): { identifiers: ActivationField[]; extras: ActivationField[] } | null {
  const { fields } = activationSpec(type);
  if (fields.via !== 'static') return null;
  return {
    identifiers: fields.identifiers(selection),
    extras: fields.extras ? fields.extras(selection) : [],
  };
}

/**
 * The far-side id to put in the field-list query. A fields arm that `splitsByIsUnique` (Gainsight PX)
 * is not scoped by the id — the membership flag is filtered client-side — so it sends none and the
 * query key stays put while the user types. Every other destination sends its id unchanged.
 */
export function fieldsQueryFarSideAudienceId(type: ActivationDestinationType, farSideAudienceId: string): string {
  const { fields } = activationSpec(type);
  return fields.via === 'endpoint' && fields.splitsByIsUnique === true ? '' : farSideAudienceId;
}

/** Gainsight PX attribute types, in the words the product uses for them (trait-mappings D20). */
const GAINSIGHT_PX_TYPE_NAMES: Readonly<Record<string, string>> = {
  STRING: 'Text',
  NUMBER: 'Number',
  BOOLEAN: 'True/False',
  DATE_TIME: 'Date',
};

/**
 * The second table's options. For an arm that `splitsByIsUnique`, the membership attribute itself is
 * not a trait (trait-mappings D20), so the row named like the (trimmed) far-side id is dropped here,
 * and each option's label carries its attribute type (`Last visit (last_visit) · Date`) so a text
 * column is not mapped to a date attribute blind. A type this table does not name is left off.
 */
export function traitOptionFields(
  type: ActivationDestinationType,
  extras: ActivationField[],
  farSideAudienceId: string,
): ActivationField[] {
  const { fields } = activationSpec(type);
  if (fields.via !== 'endpoint' || fields.splitsByIsUnique !== true) return extras;
  const flag = farSideAudienceId.trim();
  return extras
    .filter((field) => flag.length === 0 || field.name !== flag)
    .map((field) => {
      const typeName = field.type === undefined ? undefined : GAINSIGHT_PX_TYPE_NAMES[field.type];
      return typeName === undefined ? field : { ...field, label: `${field.label} · ${typeName}` };
    });
}

/**
 * The Advanced panel's starting values for a destination, from its own declared defaults.
 *
 * Derived rather than a hand-written literal: a default that disagreed with the field list would be
 * a value the user never chose being posted to the far side.
 */
export function defaultAdvancedConfig(type: ActivationDestinationType): Record<string, string> {
  const values: Record<string, string> = {};
  for (const field of advancedFieldsFor(type)) values[field.name] = field.default;
  return values;
}

/**
 * The carried keys this destination actually declared, and nothing else.
 *
 * The values arrive from a client, and they reach both a `getFields` request body and the
 * destination config Lookout writes into the customer's workspace — so "the service only reads keys
 * the spec named" has to be enforced here rather than asserted in a comment.
 */
export function pickCarried(
  type: ActivationDestinationType,
  carried: Record<string, string> | undefined,
): Record<string, string> {
  const { farSide } = activationSpec(type);
  const declared = farSide.kind === 'audience' ? (farSide.carries ?? []) : [];
  if (!carried || declared.length === 0) return {};
  const kept: Record<string, string> = {};
  for (const key of declared) {
    // Keyed by `from` — `carried` is in option-response names on both the create path (straight off
    // the picked option) and the edit path (`readCarried` maps back into them), so there is one
    // vocabulary here and the config names never leak out of the builder.
    const value = carried[key.from];
    if (typeof value === 'string' && value.length > 0) kept[key.from] = value;
  }
  return kept;
}

/**
 * Read one sync's stored `config.destination` back into wizard-shaped values.
 *
 * Delegates to the spec, like every other per-destination fact here. See
 * {@link ActivationDestinationSpec.readVdmConfig} for the absent-key convention and for why this
 * takes the sync's config rather than the destination's.
 */
export function readVdmConfig(
  type: ActivationDestinationType,
  syncDestinationConfig: Record<string, unknown>,
): VdmConfigRead {
  return activationSpec(type).readVdmConfig(syncDestinationConfig);
}

/**
 * The carried keys a destination declares, in option-response names — what
 * `rudderstack-integrations-info` needs to pull off each option.
 */
/**
 * The keys on `config.destination` this spec writes CONDITIONALLY — the ones it may legitimately omit.
 *
 * Needed because an edit merges the new config over the stored one, and a spread can add or change a
 * key but never remove one. The registry's whole convention is that an unset value is omitted rather
 * than written empty (`emitCarried`, Google's `appId`, Iterable's `audienceListType`), so without an
 * explicit removal step a key survives the edit that was supposed to drop it — and for these keys mere
 * PRESENCE is the signal. A leftover `appId` keeps telling Google the list is mobile-advertising-id
 * after the user repointed to a contact-info one; a leftover `audienceListType: 'Suppression'` inverts
 * what an Iterable sync does.
 *
 * Unconditional keys are deliberately absent: `/setup` always echoes them, so the spread overwrites
 * them and there is nothing to remove. Anything not named here — server-minted fields, keys a
 * webapp-created sync carries that this registry never modelled — is preserved untouched, which is the
 * reason the merge exists at all.
 */
export function vdmOptionalKeys(type: ActivationDestinationType): readonly string[] {
  const { farSide } = activationSpec(type);
  const carried = farSide.kind === 'audience' ? (farSide.carries ?? []).map((key) => key.to) : [];
  // The axis is emitted conditionally too (`audienceType: input.audienceType ?? undefined`). Unreachable
  // today because the update path refuses an absent axis value before it gets here — listed so a future
  // relaxation of that check does not silently make the key unremovable.
  const axis = activationSpec(type).audienceTypes ? ['audienceType'] : [];
  // Create-only: the `audience: { name, description }` object a create posts to `/setup`. An edit
  // never creates, so it never re-emits the key, and nothing Lookout saved holds it
  // (`normalizeCreatedSetupData` strips it) — listed so a stray copy on a stored config is dropped by
  // the next edit rather than carried forward as a pending create.
  const created = farSideCreateSpec(type) !== null ? ['audience'] : [];
  // NOTE: these are FIELD names, which for every current field equals its config key. FBCA's panel
  // calls one `formatData` while the config key is `disableFormat` — harmless while that key is
  // emitted unconditionally, but the moment a divergent-name field becomes conditional this would
  // name the wrong key and silently fail to remove it.
  return [...advancedFieldsFor(type).map((field) => field.name), ...carried, ...axis, ...created];
}

export function carriedKeysFor(type: ActivationDestinationType): readonly string[] {
  const { farSide } = activationSpec(type);
  return farSide.kind === 'audience' ? (farSide.carries ?? []).map((key) => key.from) : [];
}

/**
 * The Advanced fields for a destination, minus any whose `visibleWhen` does not hold.
 *
 * The universal hash question is prepended here rather than declared by each spec: it applies to
 * every destination, and its only variable is the platform name the spec already holds. Five specs
 * each restating their own `platform` in a second place is exactly how this panel once came to tell
 * Iterable users their data was going to TikTok.
 */
export function advancedFieldsFor(type: ActivationDestinationType): readonly AdvancedFieldSpec[] {
  const spec = activationSpec(type);
  return spec.hashesPii
    ? [hashPiiField(spec.platform, spec.hashesPiiWhen, spec.hashesPiiDescription), ...spec.advancedFields]
    : spec.advancedFields;
}

/** As {@link advancedFieldsFor}, with the conditional fields filtered for this selection. */
export function visibleAdvancedFields(
  type: ActivationDestinationType,
  context: AdvancedVisibility,
): readonly AdvancedFieldSpec[] {
  return advancedFieldsFor(type).filter((field) => !field.visibleWhen || field.visibleWhen(context));
}

/** The second mapping table's copy, or `null` for a destination that has only one table. */
export function extraMappingsSpec(type: ActivationDestinationType): ActivationDestinationSpec['extraMappings'] | null {
  return activationSpec(type).extraMappings ?? null;
}

/**
 * The `audienceType` axis, when the destination has one. Derived from the spec rather than tested
 * against a type name — a hardcoded `=== 'LINKEDIN_AUDIENCE'` here would be the tenth duplicate
 * declaration this registry exists to remove.
 */
export function audienceTypeAxis(type: ActivationDestinationType): AudienceTypeAxis | null {
  return activationSpec(type).audienceTypes ?? null;
}

/**
 * `data.code` stamped on every oRPC error whose `message` came off the RudderStack API.
 *
 * The retl/`integrationsInfo` clients write their messages for a developer — `POST
 * /integrationsInfo/FB_CUSTOM_AUDIENCE/destinationData returned success:false` reached a customer
 * (ACT2-608) — while Lookout's own domain errors write theirs for a user, and "This workspace is
 * not linked to a RudderStack workspace" IS the remedy. Both land on `BAD_REQUEST`, so the code is
 * the only thing that tells them apart.
 *
 * It lives here, in the module the wizard already aliases, because both halves of the split need
 * it: `mapAudienceError` stamps it and the Activate error copy reads it. Matching words in the
 * message instead — which is what shipped first — makes a backend reword silently reclassify every
 * error on the surface, with nothing to fail.
 */
export const RUDDERSTACK_API_ERROR_CODE = 'RUDDERSTACK_API_ERROR';
